import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { useVenue } from "./VenueProvider";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  addDoc,
  deleteDoc,
  writeBatch,
  deleteField,
  serverTimestamp,
  increment,
  arrayUnion,
  collection as fsCollection,
  query as fsQuery,
  where as fsWhere,
  doc as fsDoc,
  onSnapshot,
} from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { Input } from "./input";
import { Button } from "./button";
import { Badge } from "./badge";
import {
  CurrencyDollarIcon,
  UserIcon,
  PlusIcon,
  ClockIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon,
} from "@heroicons/react/20/solid";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import EspaciosPicker from "./shared/EspaciosPicker";
import { footprintDesdeResumen } from "../utils/espacios";
import { DateTime } from "luxon";
import { showAlert } from "../alertService";
import { Alert, AlertTitle, AlertDescription, AlertActions } from "./alert";
import { Timestamp } from "firebase/firestore";
import { Listbox, ListboxOption } from "./listbox";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { generateCotizacionPDF } from "../utils/generateCotizacionPDF";
import {
  getValorEvento,
  totalDesglose,
  valorVieneDeCotizacion,
  eventoPagado,
  inconsistenciasEvento,
} from "../utils/valorEvento";
import {
  resumenEvento,
  margenPorRespaldo,
  planCancelacionCostos,
  saldosAFavor,
  tieneSaldoAFavorActivo,
} from "../utils/costos";
import {
  sincronizarReservasDeEvento,
  liberarReservasDeEvento,
} from "../utils/inventario";
import { unidadesDeEvento } from "../utils/unidades";
import { metodosPagoConDefaults } from "../utils/metodosPago";
import DialogoConfirmacion, { Resumen, Fila } from "./shared/DialogoConfirmacion";
import { TIPOS_BORRABLES } from "./calendario/utils";
import { XCircleIcon as CancelarEventoIcon, TrashIcon as TrashOutlineIcon } from "@heroicons/react/24/outline";
import {
  sincronizarCotizacion,
  pendienteDeCotizacion,
} from "../utils/cotizacionProveedores";
import CotizadorDesglose from "./cotizacion/CotizadorDesglose";
import { llevaInventario, esCobrable } from "../utils/catalogo";
import ClienteEventoDialog from "./directorio/ClienteEventoDialog";
import {
  nombreCliente,
  contactoCliente,
  clienteDesdeEvento,
  ligarEventoACliente,
  desligarEventoDeCliente,
  clientesAdicionalesDe,
  camposEventoDesdeCliente,
  guardarClientesAdicionales,
  guardarRolClientePrincipal,
} from "../utils/clientes";
import EventCostosSection, { MargenPorRespaldo } from "./EventCostosSection";
import EventServiciosSection from "./EventServiciosSection";
import { spanishMonths, getYears, getDaysInMonth } from "./calendario/utils";
import { parseDateInputToTimestamp, formatCalendarDate } from "../utils/dates";
import EventProveedoresSection from "./EventProveedoresSection";
import EventClienteDepositos from "./EventClienteDepositos";
import EventArchivos from "./EventArchivos";
import ComprobanteCell from "./shared/ComprobanteCell";
import LeadNotasTimeline from "./LeadNotasTimeline";
import { esSoloLectura } from "../utils/roles";

const ACCENT = "#1b4d2e";

// Cinco pestañas: General · Cotización · Operación · Balance · Seguimientos.
// Operación responde "quién participa y qué entrega": planner, proveedores
// asignados y servicios. Balance responde "cómo va el dinero": valor,
// cobrado, depósitos, costos y reembolsos, con el resumen de utilidad
// coronándolos. Los costos se quedan en Balance aunque nazcan de los
// servicios y proveedores: el resumen de utilidad se deriva de ellos y sus
// avisos ("costos sin registrar") apuntan a la misma pestaña. Un viewer no
// ve dinero, así que para él Balance no existe y Operación es su pestaña de
// participantes. Archivos se cuelga de General (FRA-357).
const VALID_EVENT_TABS = ["general", "cotizacion", "operacion", "balance", "seguimientos"];
// Las URLs viejas siguen vivas: links guardados por los owners, y el
// `state.eventTab` con el que ProveedorPage reconstruye el regreso al evento.
const LEGACY_EVENT_TABS = {
  pagos: { tab: "balance" },
  depositos: { tab: "balance" },
  proveedores: { tab: "operacion" },
  archivos: { tab: "general" },
};

/* ── dropdown date/time selector (same pattern as calendar) ────────── */
const dtSelectBase = `
  appearance-none rounded-lg border border-zinc-950/10
  bg-white px-2 py-1.5 text-sm text-zinc-950 shadow-sm
  transition-colors focus:border-zinc-950/10 focus:outline-none focus:ring-none
  dark:border-white/10 dark:bg-white/5 dark:text-white w-full
`;
const dtSelectWrap = `
  relative after:pointer-events-none after:absolute after:right-2
  after:top-[50%] after:h-2 after:w-2 after:translate-y-[-50%]
  after:rotate-45 after:border-r-2 after:border-b-2
  after:border-zinc-500 after:content-[''] dark:after:border-zinc-400
`;

const DateTimeDropdowns = ({ label, value, onChange }) => {
  let dt = DateTime.fromFormat(value || "", "yyyy-LL-dd'T'HH:mm");
  if (!dt.isValid) dt = DateTime.fromISO(value || "");
  if (!dt.isValid) dt = DateTime.now();

  const fire = (changes) => onChange(dt.set(changes).toFormat("yyyy-LL-dd'T'HH:mm"));

  return (
    <div>
      {label && (
        <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">{label}</div>
      )}
      <div className="grid grid-cols-7 sm:grid-cols-12 gap-1.5">
        <div className={`col-span-1 sm:col-span-2 ${dtSelectWrap}`}>
          <select className={`${dtSelectBase} pr-6`} value={dt.day}
            onChange={(e) => fire({ day: parseInt(e.target.value) })}>
            {Array.from({ length: getDaysInMonth(dt.year, dt.month - 1) }, (_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}</option>
            ))}
          </select>
        </div>
        <div className={`col-span-2 sm:col-span-3 ${dtSelectWrap}`}>
          <select className={`${dtSelectBase} pr-6`} value={dt.month - 1}
            onChange={(e) => fire({ month: parseInt(e.target.value) + 1 })}>
            {spanishMonths.map((m, i) => (
              <option key={m} value={i}>{m}</option>
            ))}
          </select>
        </div>
        <div className={`col-span-2 sm:col-span-3 ${dtSelectWrap}`}>
          <select className={`${dtSelectBase} pr-6`} value={dt.year}
            onChange={(e) => fire({ year: parseInt(e.target.value) })}>
            {getYears().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className={`col-span-1 sm:col-span-2 ${dtSelectWrap}`}>
          <select className={`${dtSelectBase} pr-6`} value={dt.hour}
            onChange={(e) => fire({ hour: parseInt(e.target.value) })}>
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>{String(i).padStart(2, "0")}</option>
            ))}
          </select>
        </div>
        <div className={`col-span-1 sm:col-span-2 ${dtSelectWrap}`}>
          <select className={`${dtSelectBase} pr-6`} value={dt.minute}
            onChange={(e) => fire({ minute: parseInt(e.target.value) })}>
            {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
              <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};


const EVENT_TYPE_CONFIG = {
  scouting: { label: "Scouting", badge: "orange" },
  event: { label: "Evento", badge: "emerald" },
  reunion: { label: "Reunión", badge: "blue" },
  bloqueo: { label: "Bloqueo", badge: "red" },
};

const Section = ({ icon: Icon, title, children, className = "", action, id }) => (
  <div
    id={id}
    className={`rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm ${className}`}
  >
    <div className="flex items-center gap-2 mb-4">
      <div
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: ACCENT + "1a" }}
      >
        <Icon className="h-4 w-4" style={{ color: ACCENT }} />
      </div>
      <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {title}
      </span>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

const fmtMXN = (v) => {
  const str = String(v).replace(/,/g, "");
  const n = Number(str);
  if (isNaN(n)) return "";
  if (str.endsWith(".")) return n.toLocaleString("es-MX") + ".";
  const decMatch = str.match(/\.(\d{1,2})$/);
  if (decMatch) {
    return n.toLocaleString("es-MX", { minimumFractionDigits: decMatch[1].length, maximumFractionDigits: 2 });
  }
  return n.toLocaleString("es-MX");
};

const EditableCell = ({ value, onSave, formatDisplay, inputMode, className = "", disabled = false }) => {
  const isMoney = inputMode === "numeric";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const savingRef = React.useRef(false);
  // Sync draft when value changes externally (e.g. Firestore snapshot)
  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);
  const save = async () => {
    if (savingRef.current) return; // guard against double-fire (Enter + Blur)
    savingRef.current = true;
    const raw = isMoney ? String(draft).replace(/,/g, "").trim() : String(draft).trim();
    setEditing(false);
    if (raw !== String(value ?? "").trim()) {
      try {
        await onSave(raw);
      } catch (err) {
        showAlert(`Error al guardar. [${err.message}]`, "error");
      }
    }
    savingRef.current = false;
  };
  if (editing) {
    return (
      <div className="relative">
        {isMoney && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-zinc-400 pointer-events-none">$</span>
        )}
        <input
          type="text"
          inputMode={isMoney ? "decimal" : inputMode}
          autoFocus
          className={`w-full rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#1b4d2e] ${isMoney ? "pl-5 pr-2" : "px-2"} ${className}`}
          value={isMoney ? fmtMXN(draft) : draft}
          onChange={(e) => {
            if (isMoney) {
              const raw = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1").replace(/(\.\d{2})\d+/, "$1");
              setDraft(raw);
            } else {
              setDraft(e.target.value);
            }
          }}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); save(); }
            if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); }
          }}
        />
      </div>
    );
  }
  const display = formatDisplay ? formatDisplay(value) : value;
  if (disabled) {
    return (
      <span className="break-words" style={{ hyphens: "auto", overflowWrap: "break-word" }} lang="es">
        {display || <span className="text-zinc-400 inline-block min-w-[3rem] text-center">-</span>}
      </span>
    );
  }
  return (
    <span
      className="cursor-pointer hover:underline hover:decoration-dotted break-words"
      style={{ hyphens: "auto", overflowWrap: "break-word" }}
      lang="es"
      onClick={() => { setDraft(value ?? ""); setEditing(true); }}
      title="Clic para editar"
    >
      {display || <span className="text-zinc-400 inline-block min-w-[3rem] text-center">-</span>}
    </span>
  );
};

function getFieldLabel(key, eventType) {
  const labels = {
    bloqueo: {
      nombre: "Razón del Bloqueo",
      startTime: "Inicio",
      endTime: "Fin",
      notes: "Notas",
    },
    event: {
      nombre: "Nombre del Evento",
      cliente: "Nombre",
      apellido: "Apellido",
      clientType: "Tipo de persona",
      nombreEmpresa: "Nombre de la empresa",
      correo: "Correo",
      telefono: "Teléfono",
      numPeople: "# de personas",
      vendedor: "Vendedor",
      rfc: "RFC",
      startTime: "Inicio del Bloqueo",
      inicioMontaje: "Inicio del Montaje",
      inicioEvento: "Inicio del Evento",
      finEvento: "Fin del Evento",
      finMontaje: "Fin del Desmontaje",
      endTime: "Fin del Bloqueo",
      precioAcordado: "Precio Acordado",
      notes: "Notas",
    },
    reunion: {
      nombre: "Nombre de Reunión",
      startTime: "Inicio",
      endTime: "Fin",
      contacto: "Nombre de Contacto",
      telefono: "Teléfono de Contacto",
      notes: "Notas",
    },
    scouting: {
      nombre: "Nombre del Scouting",
      startTime: "Inicio",
      endTime: "Fin",
      contacto: "Nombre Contacto",
      telefono: "Teléfono Contacto",
      correo: "Correo Contacto",
      notes: "Notas",
    },
  };
  return labels[eventType]?.[key] || key;
}

function getEventFields(eventType) {
  switch (eventType) {
    case "bloqueo":
      return ["nombre", "startTime", "endTime", "notes"];
    case "event":
      return [
        "nombre",
        "cliente",
        "apellido",
        "clientType",
        "nombreEmpresa",
        "correo",
        "telefono",
        "numPeople",
        "vendedor",
        "rfc",
        "startTime",
        "inicioMontaje",
        "inicioEvento",
        "finEvento",
        "finMontaje",
        "endTime",
        "precioAcordado",
        "notes",
      ];
    case "reunion":
      return [
        "nombre",
        "startTime",
        "endTime",
        "contacto",
        "telefono",
        "notes",
      ];
    case "scouting":
      return [
        "nombre",
        "startTime",
        "endTime",
        "contacto",
        "telefono",
        "correo",
        "notes",
      ];
    default:
      return [];
  }
}

function formatDateTimeLocal(val, timeZone) {
  if (!val) return "";
  let dt;
  if (val.toDate) {
    dt = DateTime.fromJSDate(val.toDate(), { zone: "utc" }).setZone(timeZone);
  } else if (val instanceof Date) {
    dt = DateTime.fromJSDate(val, { zone: "utc" }).setZone(timeZone);
  } else if (typeof val === "string") {
    dt = DateTime.fromISO(val, { zone: timeZone });
    if (!dt.isValid)
      dt = DateTime.fromJSDate(new Date(val), { zone: timeZone });
  } else {
    return "";
  }
  return dt.isValid ? dt.toFormat("yyyy-LL-dd'T'HH:mm") : "";
}

function parseDateTimeLocal(val, timeZone) {
  if (!val) return null;
  const dt = DateTime.fromFormat(val, "yyyy-LL-dd'T'HH:mm", {
    zone: timeZone,
  });
  if (!dt.isValid) return null;
  return dt.toUTC().toJSDate();
}

export default function EventPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, user } = useAuth();
  // isViewer decide qué se VE (el viewer no ve dinero); soloLectura decide
  // qué se puede ESCRIBIR (viewer e inversionista). P-FRA-13.
  const isViewer = userInfo?.role === "viewer";
  const soloLectura = esSoloLectura(userInfo);
  const {
    venues,
    selectedVenueId: contextVenueId,
    events: contextEvents,
    payments: contextPayments,
    refunds: contextRefunds,
    conceptos: contextConceptos,
    proveedores: contextProveedores,
    metodosPago: contextMetodosPago,
    calendarTimezone: contextTimezone,
    venueLoading,
    venueFeatures,
    cotizacionConfig,
    loadEventArchivos,
    espaciosActivos,
    espaciosPorId,
    clientes: contextClientes,
  } = useVenue();
  const [venueId, setVenueId] = useState(null);
  // Ficha del cliente (FRA-354): ligar/desligar desde el encabezado.
  const [ligandoCliente, setLigandoCliente] = useState(false);
  // Agregar otro cliente al evento (novio y novia, productora y quien paga).
  const [agregandoCliente, setAgregandoCliente] = useState(false);
  // Mientras se escribe un cambio de clientes o roles, las tarjetas no se
  // pueden editar: la lista de adicionales se arma con el estado del render,
  // y dos cambios seguidos se pisaban (el segundo salía con el rol viejo
  // del primero).
  const [guardandoClientes, setGuardandoClientes] = useState(false);
  // Scouting, reunión y bloqueo se pueden borrar; un evento solo se cancela.
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [eventCostos, setEventCostos] = useState([]);
  const [event, setEvent] = useState(null);
  const [espaciosEdit, setEspaciosEdit] = useState([]);
  const [savingEspacios, setSavingEspacios] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [timeZone, setTimeZone] = useState("America/Mexico_City");
  const [showClashConfirm, setShowClashConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showAdvancedDates, setShowAdvancedDates] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const legacyTab = LEGACY_EVENT_TABS[rawTab];
  const resolvedTab = legacyTab
    ? legacyTab.tab
    : VALID_EVENT_TABS.includes(rawTab)
    ? rawTab
    : "general";
  // Un viewer no tiene Balance ni Cotización: un link a esas pestañas (o a
  // las viejas pagos/depositos) lo lleva a Operación en vez de a una vacía.
  const eventTab =
    isViewer && (resolvedTab === "balance" || resolvedTab === "cotizacion")
      ? "operacion"
      : resolvedTab;
  const setEventTab = useCallback(
    (newTab) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", newTab);
      if (newTab !== "balance") next.delete("sub");
      // Preserve location.state so back-nav context survives tab switches.
      setSearchParams(next, { replace: true, state: location.state });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, location.state]
  );
  // Lleva al owner desde el aviso de cancelación hasta la sección de
  // reembolsos, que vive al fondo de la pestaña Balance.
  const [scrollToReembolsos, setScrollToReembolsos] = useState(false);
  useEffect(() => {
    if (!scrollToReembolsos || eventTab !== "balance") return;
    const el = document.getElementById("seccion-reembolsos");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollToReembolsos(false);
  }, [scrollToReembolsos, eventTab]);
  const [newPago, setNewPago] = useState({
    fecha: "",
    monto: "",
    metodo: "",
    notas: "",
  });
  const [showDeletePagoConfirm, setShowDeletePagoConfirm] = useState(false);
  const [pagoToDeleteIdx, setPagoToDeleteIdx] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  // Catálogo COMPLETO del venue del evento (archivados incluidos). La
  // cotización se sincroniza contra este; el picker recibe solo los
  // cotizables (ver `conceptos` más abajo).
  const [conceptosTodos, setConceptosTodos] = useState([]);
  const [loadingConceptos, setLoadingConceptos] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [showQuickAddMetodo, setShowQuickAddMetodo] = useState(false);
  const [quickMetodo, setQuickMetodo] = useState("");
  const [showQuickAddMetodoRefund, setShowQuickAddMetodoRefund] = useState(false);
  const [quickMetodoRefund, setQuickMetodoRefund] = useState("");
  const [editingPrecioAcordado, setEditingPrecioAcordado] = useState(false);
  const [precioAcordadoInput, setPrecioAcordadoInput] = useState("");
  // Cotización PDF state
  const [exportingPDF, setExportingPDF] = useState(false);
  // Cancellation state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  // Refund state
  const [eventRefunds, setEventRefunds] = useState([]);
  const [loadingRefunds, setLoadingRefunds] = useState(true);
  const [newRefund, setNewRefund] = useState({ fecha: "", monto: "", metodo: "", notas: "" });
  const [showDeleteRefundConfirm, setShowDeleteRefundConfirm] = useState(false);
  const [refundToDeleteId, setRefundToDeleteId] = useState(null);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Navigate away if the selected venue changes and no longer contains this event
  useEffect(() => {
    if (!venueId || !contextVenueId || venueLoading) return;
    if (venueId !== contextVenueId) {
      navigate("/dashboard/eventos", { replace: true });
    }
  }, [contextVenueId, venueId, venueLoading, navigate]);

  // Find the venue containing this event
  const findVenueIdForEvent = useCallback(
    async (venuesList) => {
      for (const venue of venuesList) {
        const eventDocRef = doc(
          db,
          "venues",
          venue.id,
          "calendars",
          "default",
          "events",
          eventId
        );
        const eventSnap = await getDoc(eventDocRef);
        if (eventSnap.exists()) {
          let tz = "America/Mexico_City";
          try {
            const calendarDocRef = doc(
              db,
              "venues",
              venue.id,
              "calendars",
              "default"
            );
            const calendarSnap = await getDoc(calendarDocRef);
            if (calendarSnap.exists() && calendarSnap.data().timezone) {
              tz = calendarSnap.data().timezone;
            }
          } catch {}
          setTimeZone(tz);
          setVenueId(venue.id);
          setEvent({ id: eventSnap.id, ...eventSnap.data() });
          setEditData({ id: eventSnap.id, ...eventSnap.data() });
          return venue.id;
        }
      }
      setError("No se encontró el evento en tus venues.");
      setLoading(false);
      return null;
    },
    [eventId]
  );

  useEffect(() => {
    if (venueLoading || venues.length === 0) return;

    setLoading(true);
    setError("");

    const contextEvent = contextEvents.find((e) => e.id === eventId);
    if (contextEvent && contextVenueId) {
      setVenueId(contextVenueId);
      setTimeZone(contextTimezone);
      setEvent({ id: contextEvent.id, ...contextEvent });
      setEditData({ id: contextEvent.id, ...contextEvent });
      setLoading(false);
      return;
    }

    if (venues.length > 0) {
      findVenueIdForEvent(venues).then(() => setLoading(false));
    } else {
      setError("No tienes venues disponibles.");
      setLoading(false);
    }
  }, [
    venueLoading,
    venues,
    contextEvents,
    contextVenueId,
    contextTimezone,
    eventId,
    findVenueIdForEvent,
  ]);

  useEffect(() => {
    if (!venueId || !eventId) return;

    if (venueId === contextVenueId) {
      const pagos = contextPayments
        .filter((p) => p.eventId === eventId)
        .sort((a, b) => {
          const aDate = a.fecha?.toDate
            ? a.fecha.toDate()
            : new Date(a.fecha);
          const bDate = b.fecha?.toDate
            ? b.fecha.toDate()
            : new Date(b.fecha);
          return bDate - aDate;
        });
      setPayments(pagos);
      setLoadingPayments(false);
    }
  }, [venueId, eventId, contextVenueId, contextPayments]);

  // Costos del evento. Suscripción propia (no vía contexto) porque EventPage
  // también abre eventos de otro venue, y así el tab Balance nunca queda
  // desfasado tras una escritura.
  useEffect(() => {
    if (!venueId || !eventId || isViewer) {
      setEventCostos([]);
      return;
    }
    const costosQ = fsQuery(
      fsCollection(db, "venues", venueId, "costos"),
      fsWhere("eventId", "==", eventId)
    );
    const unsub = onSnapshot(
      costosQ,
      (snap) => setEventCostos(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Costos del evento snapshot error:", err)
    );
    return unsub;
  }, [venueId, eventId, isViewer]);

  const fetchPaymentsForEvent = useCallback(async () => {
    if (!venueId || !eventId || venueId === contextVenueId) return;

    setLoadingPayments(true);
    const pagosCol = fsCollection(db, "venues", venueId, "payments");
    const q = fsQuery(pagosCol, fsWhere("eventId", "==", eventId));
    const snap = await getDocs(q);
    const pagos = snap.docs
      .map((docu) => ({ id: docu.id, ...docu.data() }))
      .sort((a, b) => {
        const aDate = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
        const bDate = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
        return bDate - aDate;
      });
    setPayments(pagos);
    setLoadingPayments(false);
  }, [venueId, eventId, contextVenueId]);

  useEffect(() => {
    fetchPaymentsForEvent();
  }, [fetchPaymentsForEvent]);

  const [eventArchivos, setEventArchivos] = useState([]);
  const reloadEventArchivos = useCallback(async () => {
    if (!venueId || !eventId || !loadEventArchivos || isViewer) {
      setEventArchivos([]);
      return;
    }
    try {
      const items = await loadEventArchivos("default", eventId);
      setEventArchivos(items || []);
    } catch (err) {
      console.error("Error loading event archivos:", err);
      setEventArchivos([]);
    }
  }, [venueId, eventId, loadEventArchivos, isViewer]);

  useEffect(() => { reloadEventArchivos(); }, [reloadEventArchivos]);

  const archivosByLinked = useMemo(() => {
    const idx = {};
    for (const a of eventArchivos) {
      const k = a?.linkedTo?.kind;
      const id = a?.linkedTo?.id;
      if (!k || !id) continue;
      const key = `${k}:${id}`;
      if (!idx[key]) idx[key] = a;
    }
    return idx;
  }, [eventArchivos]);

  useEffect(() => {
    if (!venueId) return;
    if (venueId === contextVenueId) {
      setConceptosTodos(contextConceptos || []);
      setLoadingConceptos(false);
      return;
    }
    const fetchConceptos = async () => {
      setLoadingConceptos(true);
      const conceptosCol = collection(db, "venues", venueId, "conceptos");
      const snap = await getDocs(conceptosCol);
      setConceptosTodos(snap.docs.map((docu) => ({ id: docu.id, ...docu.data() })));
      setLoadingConceptos(false);
    };
    fetchConceptos();
  }, [venueId, contextVenueId, contextConceptos]);

  // Al picker de la cotización solo entran los renglones cotizables: los
  // archivados y los incluidos en la renta del venue existen para llevarles
  // costos, no para cobrarse.
  //
  // OJO: solo el picker. `sincronizarCotizacion` y `pendienteDeCotizacion`
  // reciben `conceptosTodos`: con la lista filtrada, archivar un servicio que
  // ya estaba cotizado hacía que la siguiente edición del desglose no lo
  // encontrara, borrara su costo automático y desasignara al proveedor.
  const conceptos = useMemo(
    () =>
      (conceptosTodos || [])
        .filter((c) => !c.archivedAt && esCobrable(c))
        .sort((a, b) =>
          (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" })
        ),
    [conceptosTodos]
  );

  // Sync metodosPago from context (with defaults — utils/metodosPago.js, la
  // misma lista que usan Cuentas, proveedores y depósitos).
  useEffect(() => {
    if (!venueId) return;
    if (venueId === contextVenueId) {
      setMetodosPago(metodosPagoConDefaults(contextMetodosPago));
      return;
    }
    const fetchMetodos = async () => {
      const col = collection(db, "venues", venueId, "metodosPago");
      const snap = await getDocs(col);
      setMetodosPago(
        metodosPagoConDefaults(snap.docs.map((docu) => ({ id: docu.id, ...docu.data() })))
      );
    };
    fetchMetodos();
  }, [venueId, contextVenueId, contextMetodosPago]);

  // Load refunds for this event (mirror payments pattern)
  useEffect(() => {
    if (!venueId || !eventId) return;
    if (venueId === contextVenueId) {
      const refs = contextRefunds
        .filter((r) => r.eventId === eventId)
        .sort((a, b) => {
          const aDate = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
          const bDate = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
          return bDate - aDate;
        });
      setEventRefunds(refs);
      setLoadingRefunds(false);
    }
  }, [venueId, eventId, contextVenueId, contextRefunds]);

  // Fallback: fetch refunds directly when event belongs to a different venue than context
  const fetchRefundsForEvent = useCallback(async () => {
    if (!venueId || !eventId || venueId === contextVenueId) return;
    setLoadingRefunds(true);
    try {
      const refundsCol = fsCollection(db, "venues", venueId, "refunds");
      const q = fsQuery(refundsCol, fsWhere("eventId", "==", eventId));
      const snap = await getDocs(q);
      const refs = snap.docs
        .map((docu) => ({ id: docu.id, ...docu.data() }))
        .sort((a, b) => {
          const aDate = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
          const bDate = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
          return bDate - aDate;
        });
      setEventRefunds(refs);
    } catch (err) {
      showAlert(`Error al cargar reembolsos. [${err.message}]`, "error");
    }
    setLoadingRefunds(false);
  }, [venueId, eventId, contextVenueId]);

  useEffect(() => {
    fetchRefundsForEvent();
  }, [fetchRefundsForEvent]);

  const totalReembolsado = eventRefunds.reduce(
    (sum, r) => sum + (Number(r.monto) || 0),
    0
  );

  const isCancelled = event?.status === "cancelado";

  // Balance del evento: valor − costos = utilidad, y el margen por proveedor
  // cruzando las líneas del desglose con sus costos. Toda la math vive en
  // utils/costos.js; aquí solo se memoiza.
  const balanceEvento = useMemo(
    () => resumenEvento({ ...(editData || {}), id: eventId }, eventCostos),
    [editData, eventId, eventCostos]
  );
  // Qué pasa con los costos si se cancela (o qué pasó, ya cancelado): lo
  // por pagar se libera, lo pagado a proveedores queda como saldo a favor.
  const planCancelacion = useMemo(
    () => planCancelacionCostos(eventCostos, eventId),
    [eventCostos, eventId]
  );
  const saldosEvento = useMemo(() => saldosAFavor(eventCostos), [eventCostos]);
  // Lo que la cotización implica y todavía no está capturado. Se DERIVA del
  // desglose en cada render, no se escribe: por eso un evento que ya existía
  // enseña su costo real desde el primer momento, sin migrar nada. Escribirlo
  // (asignar al proveedor, registrar el costo) es una acción aparte, para
  // cuando haga falta marcarlo pagado o que entre a Cuentas.
  const pendienteCotizacion = useMemo(
    () =>
      pendienteDeCotizacion({
        desglose: editData?.desglose || [],
        // El catálogo del venue DEL EVENTO, completo: mismo insumo que usa
        // sincronizarCotizacion, para que "pendiente" y "registrar" no
        // discrepen (ni entre sí ni con un admin viendo otro venue).
        conceptos: conceptosTodos,
        costos: eventCostos,
        eventId,
        asignados: event?.proveedoresAsignados || [],
        proveedores: venueId === contextVenueId ? contextProveedores : [],
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editData, eventId, eventCostos, conceptosTodos, event, venueId, contextVenueId, contextProveedores]
  );
  // La utilidad del evento cuenta SOLO los costos registrados, que es lo
  // mismo que cuentan Rentabilidad, Resumen, Estado de resultados y
  // Clientes (utils/reportes.js deriva todo de `costos`).
  //
  // Antes esta pantalla le sumaba además lo estimado de la cotización, y
  // ningún reporte lo hacía: el mismo evento tenía dos utilidades según
  // dónde lo miraras, y la que salía en el estado de resultados era la
  // equivocada. Un estimado tampoco es un compromiso —Por pagar, con razón, no
  // lo lista—, así que meterlo en la utilidad tampoco cuadraba hacia abajo.
  //
  // El "Margen 100%" que esto existía para evitar no vuelve en silencio: el
  // hueco se pinta aparte y con su botón para registrarlo (ver el aviso de
  // abajo). Y como `saveDesgloseToDB` registra los costos implicados en cada
  // guardado, el hueco solo existe hasta que alguien toca la cotización.
  const hayEstimadoSinRegistrar = pendienteCotizacion.montoEstimado > 0;
  const margenesProveedor = useMemo(
    () =>
      margenPorRespaldo({ ...(editData || {}), id: eventId }, eventCostos, {
        conceptos: contextConceptos,
        proveedores: event?.proveedoresAsignados?.map((p) => ({
          id: p.proveedorId,
          nombre: p.proveedorNombre,
        })),
        estimadoPorProveedor: pendienteCotizacion.estimadoPorProveedor,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editData, eventId, eventCostos, contextConceptos, event, pendienteCotizacion]
  );
  // Los servicios del venue con piezas físicas: son los que se pueden
  // reservar para el evento (ver utils/catalogo.js).
  const recursosDisponibles = (contextConceptos || []).filter(
    (c) => !c.archivedAt && llevaInventario(c)
  );
  const isPastEvent = (() => {
    const start = event?.startTime;
    const d = start?.toDate ? start.toDate() : start ? new Date(start) : null;
    return d instanceof Date && !isNaN(d) && d < new Date();
  })();

  // Cancel event handler
  const handleCancelEvent = async () => {
    if (!venueId || !eventId) return;
    setCancelling(true);
    try {
      const eventDocRef = doc(
        db,
        "venues",
        venueId,
        "calendars",
        "default",
        "events",
        eventId
      );
      // Las piezas reservadas se liberan primero (viven en la pieza, no en
      // el evento). Si esto falla, el evento no se cancela.
      await liberarReservasDeEvento({
        venueId,
        eventId,
        recursosAsignados: event?.recursosAsignados || [],
      });

      // Del lado del gasto (ver planCancelacionCostos en utils/costos.js):
      // lo POR PAGAR ya no se debe y se elimina; lo YA PAGADO a un proveedor
      // queda como saldo a favor con él, que se resuelve desde su ficha.
      const ahora = Timestamp.now();
      const batch = writeBatch(db);
      planCancelacion.liberar.forEach((c) => {
        batch.delete(fsDoc(db, "venues", venueId, "costos", c.id));
      });
      planCancelacion.saldos.forEach((c) => {
        batch.update(fsDoc(db, "venues", venueId, "costos", c.id), {
          saldoAFavor: {
            estado: "activo",
            monto: Number(c.monto) || 0,
            eventId,
            eventoNombre: event?.nombre || "",
            createdAt: ahora,
          },
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        });
      });
      const costosLiberados = {
        total: planCancelacion.totalLiberar,
        count: planCancelacion.liberar.length,
        at: ahora,
        items: planCancelacion.liberar.map((c) => ({
          id: c.id,
          concepto: c.concepto || "",
          monto: Number(c.monto) || 0,
          categoria: c.categoria || null,
          proveedorId: c.proveedorId || null,
          proveedorNombre: c.proveedorNombre || null,
        })),
      };
      const cancelPatch = {
        status: "cancelado",
        cancelledAt: ahora,
        cancelledReason: cancelReason || "",
        costosLiberadosAlCancelar: costosLiberados,
      };
      batch.update(eventDocRef, cancelPatch);
      await batch.commit();

      setEvent((prev) => ({ ...prev, ...cancelPatch }));
      setEditData((prev) => ({ ...prev, ...cancelPatch }));
      setShowCancelConfirm(false);
      setCancelReason("");
      showAlert("Evento cancelado correctamente", "success");
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_CANCEL,
        entity: "event",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: {
          reason: cancelReason || "",
          costosLiberados: costosLiberados.count,
          montoLiberado: costosLiberados.total,
          saldosAFavor: planCancelacion.saldos.length,
          montoSaldoAFavor: planCancelacion.totalSaldos,
        },
      }, userInfo, user);
    } catch (err) {
      showAlert(`Error al cancelar el evento. [${err.message}]`, "error");
    }
    setCancelling(false);
  };

  // Refund handlers
  // Los botones de % calculan sobre lo pagado (así se expresan las políticas
  // de cancelación) pero topan en lo que queda sin reembolsar, para no
  // proponer una devolución mayor que el saldo. También precargan la fecha
  // de hoy si el campo está vacío: un clic menos para el caso típico.
  const aplicarPorcentajeReembolso = (pct) => {
    const monto = Math.min(
      Math.round((totalPagado * pct) / 100),
      pendienteReembolso
    );
    const hoy = new Date();
    const fechaHoy = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;
    setNewRefund((r) => ({
      ...r,
      monto: String(monto),
      fecha: r.fecha || fechaHoy,
    }));
  };
  const handleAddRefund = async () => {
    if (!newRefund.fecha) {
      showAlert("Por favor ingresa la fecha del reembolso.", "error");
      return;
    }
    if (
      !newRefund.monto ||
      isNaN(Number(newRefund.monto)) ||
      Number(newRefund.monto) <= 0
    ) {
      showAlert("Por favor ingresa un monto válido mayor a 0.", "error");
      return;
    }
    const refundAmount = Number(newRefund.monto);
    if (totalReembolsado + refundAmount > totalPagado) {
      showAlert("El total de reembolsos no puede exceder el total pagado.", "error");
      return;
    }
    const fechaTimestamp = parseDateInputToTimestamp(newRefund.fecha, timeZone);
    if (!fechaTimestamp) {
      showAlert("Fecha de reembolso inválida.", "error");
      return;
    }
    try {
      const refundToSave = {
        ...newRefund,
        fecha: fechaTimestamp,
        monto: refundAmount,
        eventId,
        createdAt: Timestamp.now(),
      };
      if (venueId && eventId) {
        const batch = writeBatch(db);
        const refundDocRef = fsDoc(db, "venues", venueId, "refunds", doc(fsCollection(db, "venues", venueId, "refunds")).id);
        batch.set(refundDocRef, refundToSave);
        const eventDocRef = doc(
          db,
          "venues",
          venueId,
          "calendars",
          "default",
          "events",
          eventId
        );
        batch.update(eventDocRef, { totalReembolsado: increment(refundAmount) });
        await batch.commit();
        logBitacora(venueId, {
          action: BITACORA_ACTIONS.REFUND_CREATE,
          entity: "refund",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: { monto: refundAmount, metodo: newRefund.metodo || "" },
        }, userInfo, user);
      }
      setNewRefund({ fecha: "", monto: "", metodo: "", notas: "" });
    } catch (err) {
      showAlert(`Error al registrar reembolso. [${err.message}]`, "error");
    }
  };

  const handleRemoveRefund = (refundId) => {
    setRefundToDeleteId(refundId);
    setShowDeleteRefundConfirm(true);
  };
  const confirmRemoveRefund = async () => {
    if (!refundToDeleteId || !venueId) return;
    try {
      const refund = eventRefunds.find((r) => r.id === refundToDeleteId);
      const linkedArchivo = archivosByLinked[`refund:${refundToDeleteId}`];
      const batch = writeBatch(db);
      batch.delete(fsDoc(db, "venues", venueId, "refunds", refundToDeleteId));
      if (refund) {
        const eventDocRef = doc(
          db,
          "venues",
          venueId,
          "calendars",
          "default",
          "events",
          eventId
        );
        batch.update(eventDocRef, { totalReembolsado: increment(-(Number(refund.monto) || 0)) });
      }
      if (linkedArchivo) {
        batch.delete(fsDoc(db, "venues", venueId, "calendars", "default", "events", eventId, "archivos", linkedArchivo.id));
      }
      await batch.commit();
      if (linkedArchivo?.storagePath) {
        try { await deleteObject(storageRef(storage, linkedArchivo.storagePath)); }
        catch (err) {
          if (err?.code !== "storage/object-not-found") {
            console.error("Error deleting refund comprobante blob:", err);
          }
        }
      }
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.REFUND_DELETE,
        entity: "refund",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: { monto: Number(refund?.monto) || 0, refundId: refundToDeleteId },
      }, userInfo, user);
      setShowDeleteRefundConfirm(false);
      setRefundToDeleteId(null);
      if (linkedArchivo) reloadEventArchivos();
    } catch (err) {
      showAlert(`Error al eliminar reembolso. [${err.message}]`, "error");
    }
  };
  const cancelRemoveRefund = () => {
    setShowDeleteRefundConfirm(false);
    setRefundToDeleteId(null);
  };

  // Reactivate a cancelled event — deletes all refunds in a batch
  const handleReactivateEvent = async () => {
    if (!venueId || !eventId) return;
    setReactivating(true);
    try {
      const batch = writeBatch(db);
      // Delete all refunds for this event
      for (const refund of eventRefunds) {
        batch.delete(fsDoc(db, "venues", venueId, "refunds", refund.id));
      }
      // Remove cancelled fields from event
      const eventDocRef = doc(
        db,
        "venues",
        venueId,
        "calendars",
        "default",
        "events",
        eventId
      );
      batch.update(eventDocRef, {
        status: deleteField(),
        cancelledAt: deleteField(),
        cancelledReason: deleteField(),
        totalReembolsado: deleteField(),
        costosLiberadosAlCancelar: deleteField(),
      });
      // Los saldos a favor que abrió la cancelación y siguen sin resolver
      // se cierran: el pago vuelve a ser simplemente el costo del evento.
      // Los ya aplicados/devueltos no se tocan — eso sí pasó.
      eventCostos.filter(tieneSaldoAFavorActivo).forEach((c) => {
        batch.update(fsDoc(db, "venues", venueId, "costos", c.id), {
          saldoAFavor: deleteField(),
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        });
      });
      await batch.commit();

      // Las piezas se vuelven a reservar. Las que otro evento tomó mientras
      // este estaba cancelado se retiran del evento y se avisa.
      let retiradas = [];
      try {
        const r = await sincronizarReservasDeEvento({
          venueId,
          eventId,
          events: contextEvents,
          alConflicto: "retirar",
        });
        retiradas = r?.retiradas || [];
      } catch (err) {
        console.error("No se pudieron restaurar las reservas de piezas:", err);
      }
      if (retiradas.length > 0) {
        showAlert(
          `Se reactivó, pero ${retiradas.length === 1 ? "una pieza ya está" : `${retiradas.length} piezas ya están`} en otro evento y se quitó de este: ${retiradas
            .map((c) => `${c.etiqueta || "pieza"} (${c.evento?.nombre || "otro evento"})`)
            .join(", ")}.`,
          "error"
        );
      }
      // Los costos por pagar que la cancelación liberó no vuelven solos; los
      // automáticos de la cotización sí, porque el desglose sigue ahí.
      if (Array.isArray(event?.desglose) && event.desglose.length > 0) {
        await aplicarCotizacionAlGasto(event.desglose, { silencioso: true });
      }
      const cleared = { status: undefined, cancelledAt: undefined, cancelledReason: undefined, totalReembolsado: undefined };
      setEvent((prev) => {
        const next = { ...prev, ...cleared };
        delete next.status; delete next.cancelledAt; delete next.cancelledReason; delete next.totalReembolsado;
        return next;
      });
      setEditData((prev) => {
        const next = { ...prev, ...cleared };
        delete next.status; delete next.cancelledAt; delete next.cancelledReason; delete next.totalReembolsado;
        return next;
      });
      setEventRefunds([]);
      setShowReactivateConfirm(false);
      showAlert("Evento reactivado correctamente", "success");
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_REACTIVATE,
        entity: "event",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: {},
      }, userInfo, user);
    } catch (err) {
      showAlert(`Error al reactivar el evento. [${err.message}]`, "error");
    }
    setReactivating(false);
  };

  const handleAddPago = async () => {
    if (!newPago.fecha) {
      showAlert("Por favor ingresa la fecha del pago.", "error");
      return;
    }
    if (
      !newPago.monto ||
      isNaN(Number(newPago.monto)) ||
      Number(newPago.monto) <= 0
    ) {
      showAlert("Por favor ingresa un monto válido mayor a 0.", "error");
      return;
    }
    const fechaTimestamp = parseDateInputToTimestamp(newPago.fecha, timeZone);
    if (!fechaTimestamp) {
      showAlert("Fecha de pago inválida.", "error");
      return;
    }
    const pagoToSave = {
      ...newPago,
      fecha: fechaTimestamp,
      monto: Number(newPago.monto),
      eventId,
      createdAt: Timestamp.now(),
    };
    try {
      if (venueId && eventId) {
        const pagosCol = fsCollection(db, "venues", venueId, "payments");
        await addDoc(pagosCol, pagoToSave);
        logBitacora(venueId, {
          action: BITACORA_ACTIONS.PAYMENT_CREATE,
          entity: "payment",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: { monto: Number(newPago.monto), metodo: newPago.metodo || "" },
        }, userInfo, user);
      }
      setNewPago({ fecha: "", monto: "", metodo: "", notas: "" });
    } catch (err) {
      showAlert(`Error al registrar pago. [${err.message}]`, "error");
    }
  };

  const handleRemovePago = (pagoId) => {
    setPagoToDeleteIdx(pagoId);
    setShowDeletePagoConfirm(true);
  };
  const confirmRemovePago = async () => {
    if (!pagoToDeleteIdx || !venueId) return;
    const deletedPago = payments.find((p) => p.id === pagoToDeleteIdx);
    const linkedArchivo = archivosByLinked[`payment:${pagoToDeleteIdx}`];
    const pagoDocRef = fsDoc(
      db,
      "venues",
      venueId,
      "payments",
      pagoToDeleteIdx
    );
    await deleteDoc(pagoDocRef);
    if (linkedArchivo) {
      try {
        await deleteDoc(fsDoc(db, "venues", venueId, "calendars", "default", "events", eventId, "archivos", linkedArchivo.id));
      } catch (err) {
        console.error("Error deleting payment comprobante doc:", err);
      }
      if (linkedArchivo.storagePath) {
        try { await deleteObject(storageRef(storage, linkedArchivo.storagePath)); }
        catch (err) {
          if (err?.code !== "storage/object-not-found") {
            console.error("Error deleting payment comprobante blob:", err);
          }
        }
      }
      reloadEventArchivos();
    }
    logBitacora(venueId, {
      action: BITACORA_ACTIONS.PAYMENT_DELETE,
      entity: "payment",
      entityId: eventId,
      entityName: event?.nombre || "",
      details: { monto: Number(deletedPago?.monto) || 0, pagoId: pagoToDeleteIdx },
    }, userInfo, user);
    setShowDeletePagoConfirm(false);
    setPagoToDeleteIdx(null);
  };
  const cancelRemovePago = () => {
    setShowDeletePagoConfirm(false);
    setPagoToDeleteIdx(null);
  };

  const clienteDelEvento = event?.clienteId
    ? (contextClientes || []).find((c) => c.id === event.clienteId) || null
    : null;
  const ligarCliente = async (cliente, { rellenar = false } = {}) => {
    if (!venueId || !eventId || !cliente?.id) return;
    try {
      const cambiaCliente = cliente.id !== event?.clienteId;
      await ligarEventoACliente({
        venueId,
        eventId,
        cliente,
        rellenarCampos: rellenar,
        limpiarRol: cambiaCliente,
      });
      const patch = rellenar
        ? {
            clienteId: cliente.id,
            cliente: cliente.nombre || "",
            apellido: cliente.apellido || "",
            clientType: cliente.clientType || "fisica",
            nombreEmpresa: cliente.nombreEmpresa || "",
            correo: cliente.correo || "",
            telefono: cliente.telefono || "",
            rfc: cliente.rfc || "",
          }
        : { clienteId: cliente.id };
      if (cambiaCliente) patch.clienteRol = "";
      setEvent((prev) => ({ ...prev, ...patch }));
      setEditData((prev) => ({ ...prev, ...patch }));
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_CLIENTE_LINK,
        entity: "event",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: { clienteId: cliente.id, clienteNombre: nombreCliente(cliente) },
      }, userInfo, user);
      setLigandoCliente(false);
      showAlert("Evento ligado al cliente", "success");
    } catch (err) {
      showAlert(`No se pudo ligar. [${err.message}]`, "error");
    }
  };
  // Ligar la ficha recién creada desde el diálogo. Crear y ligar son dos
  // escrituras: si la segunda falla, la ficha ya existe y hay que decirlo,
  // no fingir que no pasó nada.
  const ligarClienteCreado = async (clienteId) => {
    try {
      await ligarEventoACliente({ venueId, eventId, cliente: { id: clienteId }, limpiarRol: true });
      setEvent((prev) => ({ ...prev, clienteId, clienteRol: "" }));
      setEditData((prev) => ({ ...prev, clienteId, clienteRol: "" }));
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_CLIENTE_LINK,
        entity: "event",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: { clienteId, creado: true },
      }, userInfo, user);
      setLigandoCliente(false);
    } catch (err) {
      showAlert(`Se creó el cliente pero no se pudo ligar al evento. [${err.message}]`, "error");
    }
  };
  const desligarCliente = async () => {
    if (!venueId || !eventId) return;
    try {
      await desligarEventoDeCliente({ venueId, eventId });
      setEvent((prev) => ({ ...prev, clienteId: null, clienteRol: "" }));
      setEditData((prev) => ({ ...prev, clienteId: null, clienteRol: "" }));
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_CLIENTE_UNLINK,
        entity: "event",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: {},
      }, userInfo, user);
    } catch (err) {
      showAlert(`No se pudo desligar. [${err.message}]`, "error");
    }
  };

  // ── Varios clientes por evento ──────────────────────────────────────
  // La principal (`clienteId`) carga con el dinero en su historial; las
  // demás participan con un rol de texto libre (`clientesAdicionales`).
  const clientesAdicionales = clientesAdicionalesDe(event);
  // Los campos sueltos del evento (lo que sale en la cotización) frente a
  // lo que dice el cliente en el directorio. Se compara solo lo que el
  // cliente puede aportar; "difieren" avisa, no corrige solo.
  const datosDifierenDelCliente = (() => {
    if (!clienteDelEvento) return false;
    const c = camposEventoDesdeCliente(clienteDelEvento);
    const norm = (v) => String(v ?? "").trim().toLowerCase();
    return ["cliente", "apellido", "correo", "telefono", "nombreEmpresa", "rfc"].some(
      (k) => norm(c[k]) !== norm(event?.[k])
    );
  })();
  const fichaPorId = (id) => (contextClientes || []).find((c) => c.id === id) || null;
  const escribirAdicionales = async (lista, bitacora) => {
    setGuardandoClientes(true);
    let limpio;
    try {
      limpio = await guardarClientesAdicionales({
        venueId,
        eventId,
        clientesAdicionales: lista,
      });
    } finally {
      setGuardandoClientes(false);
    }
    setEvent((prev) => ({ ...prev, clientesAdicionales: limpio }));
    setEditData((prev) => ({ ...prev, clientesAdicionales: limpio }));
    if (bitacora) {
      logBitacora(venueId, {
        ...bitacora,
        entity: "event",
        entityId: eventId,
        entityName: event?.nombre || "",
      }, userInfo, user);
    }
  };
  const agregarClienteAdicional = async (clienteId, { creada = false, cliente } = {}) => {
    if (!venueId || !eventId || !clienteId) return;
    if (clienteId === event?.clienteId || clientesAdicionales.some((x) => x.clienteId === clienteId)) {
      showAlert("Ese cliente ya está en el evento", "error");
      return;
    }
    try {
      await escribirAdicionales(
        [...clientesAdicionales, { clienteId, rol: "" }],
        {
          action: BITACORA_ACTIONS.EVENT_CLIENTE_LINK,
          details: {
            clienteId,
            clienteNombre: nombreCliente(cliente || fichaPorId(clienteId) || {}),
            adicional: true,
            creada,
          },
        }
      );
      setAgregandoCliente(false);
      showAlert(creada ? "Cliente creado y agregado al evento" : "Cliente agregado al evento", "success");
    } catch (err) {
      showAlert(`No se pudo agregar. [${err.message}]`, "error");
    }
  };
  const quitarClienteAdicional = async (clienteId) => {
    try {
      await escribirAdicionales(
        clientesAdicionales.filter((x) => x.clienteId !== clienteId),
        {
          action: BITACORA_ACTIONS.EVENT_CLIENTE_UNLINK,
          details: { clienteId, clienteNombre: nombreCliente(fichaPorId(clienteId) || {}), adicional: true },
        }
      );
    } catch (err) {
      showAlert(`No se pudo quitar. [${err.message}]`, "error");
    }
  };
  const cambiarRolAdicional = async (clienteId, rol) => {
    await escribirAdicionales(
      clientesAdicionales.map((x) => (x.clienteId === clienteId ? { ...x, rol } : x)),
      {
        action: BITACORA_ACTIONS.EVENT_CLIENTE_ROL,
        details: { clienteId, clienteNombre: nombreCliente(fichaPorId(clienteId) || {}), rol },
      }
    );
  };
  const cambiarRolPrincipal = async (rol) => {
    setGuardandoClientes(true);
    try {
      await guardarRolClientePrincipal({ venueId, eventId, rol });
    } finally {
      setGuardandoClientes(false);
    }
    setEvent((prev) => ({ ...prev, clienteRol: rol }));
    setEditData((prev) => ({ ...prev, clienteRol: rol }));
    logBitacora(venueId, {
      action: BITACORA_ACTIONS.EVENT_CLIENTE_ROL,
      entity: "event",
      entityId: eventId,
      entityName: event?.nombre || "",
      details: { clienteId: event?.clienteId || null, clienteNombre: nombreCliente(clienteDelEvento || {}), rol },
    }, userInfo, user);
  };

  const handleChange = (key, value) => {
    if (key === "precioAcordado") {
      const numeric = value.replace(/,/g, "");
      setEditData((prev) => ({ ...prev, [key]: numeric }));
    } else if (!showAdvancedDates && (key === "startTime" || key === "endTime")) {
      // Auto-adjust hidden intermediate dates to stay consistent
      setEditData((prev) => {
        const next = { ...prev, [key]: value };
        if (key === "startTime") {
          // Shift inicioMontaje & inicioEvento to match new start
          if (prev.inicioMontaje) next.inicioMontaje = value;
          if (prev.inicioEvento) next.inicioEvento = value;
        }
        if (key === "endTime") {
          // Shift finEvento & finMontaje to match new end
          if (prev.finEvento) next.finEvento = value;
          if (prev.finMontaje) next.finMontaje = value;
        }
        return next;
      });
    } else {
      setEditData((prev) => ({ ...prev, [key]: value }));
    }
  };

  // Resolve an editData date field to a JS Date
  const resolveDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
    if (val.toDate) return val.toDate();
    if (typeof val === "string") {
      const d = parseDateTimeLocal(val, timeZone);
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    return null;
  };

  // Find events that overlap with the current edited dates
  const getClashingEvents = () => {
    if (!editData) return [];
    const editStart = resolveDate(editData.startTime);
    const editEnd = resolveDate(editData.endTime);
    if (!editStart || !editEnd) return [];

    return contextEvents.filter((e) => {
      if (e.id === eventId) return false; // skip self
      if (e.status === "cancelado") return false; // skip cancelled
      let eStart = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
      let eEnd = e.endTime?.toDate ? e.endTime.toDate() : new Date(e.endTime);
      if (isNaN(eStart?.getTime()) || isNaN(eEnd?.getTime())) return false;
      return editStart < eEnd && editEnd > eStart;
    });
  };

  const clashingEvents = isEditing ? getClashingEvents() : [];

  // Check sequential date order: startTime <= inicioMontaje <= inicioEvento <= finEvento <= finMontaje <= endTime
  const DATE_SEQUENCE = [
    ["startTime", "inicioMontaje", "Inicio del montaje debe ser posterior al inicio del bloqueo"],
    ["inicioMontaje", "inicioEvento", "Inicio del evento debe ser posterior al inicio del montaje"],
    ["inicioEvento", "finEvento", "Fin del evento debe ser posterior al inicio del evento"],
    ["finEvento", "finMontaje", "Fin del desmontaje debe ser posterior al fin del evento"],
    ["finMontaje", "endTime", "Fin del bloqueo debe ser posterior al fin del desmontaje"],
    ["startTime", "endTime", "Fin del bloqueo debe ser posterior al inicio del bloqueo"],
  ];
  const dateWarnings = isEditing && editData
    ? DATE_SEQUENCE
        .filter(([a, b]) => editData[a] && editData[b])
        .filter(([a, b]) => {
          const dA = resolveDate(editData[a]);
          const dB = resolveDate(editData[b]);
          return dA && dB && dB < dA;
        })
        .map(([,, msg]) => msg)
    : [];
  const hasDateErrors = dateWarnings.length > 0;

  // Detect conflicts that would arise from reactivating a cancelled event
  const reactivationClashes = (() => {
    if (!isCancelled || !event) return [];
    const evStart = event.startTime?.toDate ? event.startTime.toDate() : new Date(event.startTime);
    const evEnd = event.endTime?.toDate ? event.endTime.toDate() : new Date(event.endTime);
    if (isNaN(evStart?.getTime()) || isNaN(evEnd?.getTime())) return [];
    return contextEvents.filter((e) => {
      if (e.id === eventId) return false;
      if (e.status === "cancelado") return false;
      let eStart = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
      let eEnd = e.endTime?.toDate ? e.endTime.toDate() : new Date(e.endTime);
      if (isNaN(eStart?.getTime()) || isNaN(eEnd?.getTime())) return false;
      return evStart < eEnd && evEnd > eStart;
    });
  })();

  /* ── espacios del evento ───────────────────────────────────── */
  const espaciosEvento = useMemo(() => event?.espaciosIds || [], [event]);
  // Activos + los que el evento ya traía aunque hoy estén inactivos, para
  // poder quitarlos (mismo criterio que EventDialog).
  const opcionesEspaciosEvento = useMemo(
    () => [
      ...espaciosActivos,
      ...espaciosEvento
        .filter((id) => !espaciosActivos.some((e) => e.id === id))
        .map((id) => espaciosPorId[id] || { id, nombre: id }),
    ],
    [espaciosActivos, espaciosPorId, espaciosEvento]
  );
  useEffect(() => {
    setEspaciosEdit(espaciosEvento);
  }, [espaciosEvento]);
  const espaciosCambiaron =
    JSON.stringify([...espaciosEdit].sort()) !==
    JSON.stringify([...espaciosEvento].sort());

  const handleSaveEspacios = async () => {
    if (!venueId || !eventId) return;
    setSavingEspacios(true);
    try {
      const ref = doc(db, "venues", venueId, "calendars", "default", "events", eventId);
      // Deseleccionar todo devuelve el evento a "todo el venue": se borran
      // los campos, como un evento legacy (contrato de utils/espacios.js).
      if (espaciosEdit.length > 0) {
        const fp = footprintDesdeResumen(espaciosEdit, espaciosPorId);
        await updateDoc(ref, {
          espaciosIds: espaciosEdit,
          unidadesOcupadas: fp ? [...fp].sort() : [],
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(ref, {
          espaciosIds: deleteField(),
          unidadesOcupadas: deleteField(),
          updatedAt: serverTimestamp(),
        });
      }
      showAlert("Espacios actualizados", "success");
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.EVENT_EDIT,
          entity: "event",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: {
            espacios: espaciosEdit.map((id) => espaciosPorId[id]?.nombre || id),
          },
        },
        userInfo,
        user
      );
    } catch (err) {
      console.error("Error actualizando espacios:", err);
      showAlert("Error al actualizar los espacios", "error");
    }
    setSavingEspacios(false);
  };

  const handleSave = async () => {
    if (!venueId || !editData) return;
    setSaving(true);
    setError("");
    try {
      const eventDocRef = doc(
        db,
        "venues",
        venueId,
        "calendars",
        "default",
        "events",
        eventId
      );
      const eventType = editData.eventType;
      const allowedFields = new Set(
        getEventFields(eventType).concat(["eventType", "id"])
      );
      const updateData = {};
      Object.keys(editData).forEach((key) => {
        if (allowedFields.has(key)) {
          updateData[key] = editData[key];
        }
      });
      [
        "startTime",
        "inicioMontaje",
        "inicioEvento",
        "finEvento",
        "finMontaje",
        "endTime",
      ].forEach((key) => {
        if (!(key in updateData)) return;
        const val = updateData[key];
        if (val instanceof Date) {
          return;
        } else if (typeof val === "string" && val.trim() !== "") {
          const d = parseDateTimeLocal(val, timeZone);
          if (d instanceof Date && !isNaN(d.getTime())) {
            updateData[key] = d;
          } else {
            delete updateData[key];
          }
        } else {
          delete updateData[key];
        }
      });
      const totalPagadoSum = payments.reduce(
        (sum, p) => sum + (Number(p.monto) || 0),
        0
      );
      updateData.totalPagado = totalPagadoSum;
      updateData.pagado = eventoPagado(editData, totalPagadoSum);

      // Mover el evento de fecha revalida sus piezas reservadas contra el
      // rango nuevo (y mueve sus reservas). Si otra fecha ya las tiene, no
      // se guarda: el owner decide qué pieza suelta.
      const aMs = (v) =>
        v instanceof Date ? v.getTime() : v?.toDate ? v.toDate().getTime() : v ? new Date(v).getTime() : NaN;
      const fechaCambia = ["startTime", "endTime"].some(
        (k) => k in updateData && aMs(updateData[k]) !== aMs(event?.[k])
      );
      if (fechaCambia && unidadesDeEvento(event).length > 0) {
        const r = await sincronizarReservasDeEvento({
          venueId,
          eventId,
          start: updateData.startTime || event?.startTime,
          end: updateData.endTime || event?.endTime || updateData.startTime || event?.startTime,
          events: contextEvents,
        });
        if (!r.ok) {
          showAlert(
            `No se guardó: en la fecha nueva ${r.conflictos
              .map((c) => `${c.etiqueta || "una pieza"} ya está en ${c.evento?.nombre || "otro evento"}`)
              .join("; ")}. Quita esa pieza del evento o elige otra fecha.`,
            "error"
          );
          setSaving(false);
          return false;
        }
      }

      await updateDoc(eventDocRef, updateData);
      setEvent({ ...editData });
      showAlert("Cambios guardados correctamente", "success");
      // Compute field-level diff for audit log
      const skipFields = new Set(["startTime", "inicioMontaje", "inicioEvento", "finEvento", "finMontaje", "endTime", "notes", "eventType", "id", "precioAcordado", "totalPagado", "pagado"]);
      const changes = [];
      for (const key of getEventFields(eventType)) {
        if (skipFields.has(key)) continue;
        const oldVal = String(event[key] || "");
        const newVal = String(editData[key] || "");
        if (oldVal !== newVal) {
          changes.push({ field: getFieldLabel(key, eventType), from: oldVal || "-", to: newVal || "-" });
        }
      }
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_EDIT,
        entity: "event",
        entityId: eventId,
        entityName: editData?.nombre || "",
        details: changes.length > 0 ? { changes } : {},
      }, userInfo, user);
    } catch (err) {
      setError("");
      showAlert(`Error al guardar los cambios. [${err.message}]`, "error");
      setSaving(false);
      return false;
    }
    setSaving(false);
    return true;
  };

  // Solo scouting, reunión y bloqueo: no cargan dinero ni depósitos. Un
  // evento no se borra (firestore.rules tampoco lo deja a un owner).
  const borrarDelCalendario = async () => {
    if (!venueId || !eventId || !TIPOS_BORRABLES.includes(event?.eventType)) return;
    setBorrando(true);
    try {
      await deleteDoc(fsDoc(db, "venues", venueId, "calendars", "default", "events", eventId));
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_DELETE,
        entity: "event",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: { eventType: event?.eventType },
      }, userInfo, user);
      showAlert("Eliminado del calendario", "success");
      navigate("/dashboard/eventos", { replace: true });
    } catch (err) {
      showAlert(`No se pudo eliminar. [${err.message}]`, "error");
      setBorrando(false);
    }
  };

  const totalPagado = payments.reduce(
    (sum, p) => sum + (Number(p.monto) || 0),
    0
  );
  const pendienteReembolso = Math.max(0, totalPagado - totalReembolsado);
  // El valor efectivo del evento: la cotización manda cuando existe;
  // precioAcordado es el fallback (ver utils/valorEvento.js).
  const valorEvento = getValorEvento(editData || {});
  const balance = valorEvento - totalPagado;
  const inconsistencias = inconsistenciasEvento(editData || {}, totalPagado);

  useEffect(() => {
    if (!venueId || !eventId || editData == null) return;
    // Skip if venue context has changed (component is about to navigate away)
    if (venueId !== contextVenueId) return;
    // Solo lectura no persiste nada: las reglas rechazarían la escritura.
    if (soloLectura) return;
    const updatePagadoFlag = async () => {
      try {
        const eventDocRef = fsDoc(
          db,
          "venues",
          venueId,
          "calendars",
          "default",
          "events",
          eventId
        );
        const pagado = eventoPagado(editData, totalPagado);
        await updateDoc(eventDocRef, {
          totalPagado,
          pagado,
        });
        setEvent((prev) => ({ ...prev, totalPagado, pagado }));
        setEditData((prev) => ({ ...prev, totalPagado, pagado }));
      } catch (err) {
        console.warn("updatePagadoFlag failed:", err);
      }
    };
    updatePagadoFlag();
    // eslint-disable-next-line
  }, [totalPagado, venueId, eventId, valorEvento, contextVenueId, soloLectura]);


  function displayValue(key, value) {
    if (
      key.includes("Time") ||
      key.includes("inicio") ||
      key.includes("fin")
    ) {
      if (!value) return "-";
      let dateObj = value?.toDate ? value.toDate() : new Date(value);
      if (isNaN(dateObj.getTime())) return "-";
      // Zona del venue (contextTimezone, ya en scope desde useVenue). Sin ella
      // el evento se formatea en la zona del navegador de quien mira.
      const timeZone = contextTimezone;
      const weekday = dateObj.toLocaleDateString("es-MX", {
        weekday: "long",
        timeZone,
      });
      const dateStr = dateObj.toLocaleDateString("es-MX", { timeZone });
      const timeStr = dateObj.toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone,
      });
      return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${dateStr}, ${timeStr}`;
    }
    if (key === "precioAcordado") {
      return value ? `$${Number(value).toLocaleString("es-MX")}` : "-";
    }
    return value || "-";
  }

  const desglose = editData?.desglose || [];
  const ivaEnabled = editData?.ivaEnabled || false;

  const saveIvaEnabled = async (val) => {
    if (!venueId || !eventId) return;
    const eventDocRef = doc(db, "venues", venueId, "calendars", "default", "events", eventId);
    const update = { ivaEnabled: val };
    // El toggle de IVA cambia el total de la cotización — mantener el precio
    // acordado sincronizado con ella.
    const nuevoTotal = totalDesglose(editData?.desglose, val);
    if (Array.isArray(editData?.desglose) && editData.desglose.length > 0 && nuevoTotal > 0) {
      update.precioAcordado = nuevoTotal;
    }
    await updateDoc(eventDocRef, update);
    setEvent((prev) => ({ ...prev, ...update }));
    setEditData((prev) => ({ ...prev, ...update }));
    logBitacora(venueId, {
      action: BITACORA_ACTIONS.EVENT_TOGGLE_IVA,
      entity: "event",
      entityId: eventId,
      entityName: event?.nombre || "",
      details: { ivaEnabled: val, ...(update.precioAcordado != null ? { precioAcordado: update.precioAcordado } : {}) },
    }, userInfo, user);
  };

  // --- Cotización PDF export ---
  const currentVenue = venues.find((v) => v.id === venueId) || null;
  const isPremiumCotizaciones = venueFeatures?.premiumCotizaciones === true;
  const cotizacionHistory = event?.cotizacionHistory || [];

  const handleExportPDF = async () => {
    // Solo lectura no genera PDFs (P-FRA-13): el botón no se muestra.
    if (soloLectura) return;
    if (!venueId || !eventId || exportingPDF) return;
    setExportingPDF(true);
    try {
      const historyCount = (event?.cotizacionHistory || []).length + 1;
      const folio = `COT-${String(historyCount).padStart(4, "0")}`;

      // If not premium, use free defaults (only nombre carries over)
      const resolvedConfig = isPremiumCotizaciones
        ? cotizacionConfig
        : {
            primaryColor: "#1b4d2e",
            accentColor: "#1b4d2e",
            secondaryColor: "#f0fdf4",
            logoUrl: "",
            logoSize: 60,
            headerHeight: 80,
            showVenueName: true,
            direccion: "",
            telefono: "",
            correo: "",
            vigencia: "30 días",
            leyendaExtra: "",
            nombreCotizacion: cotizacionConfig?.nombreCotizacion || "",
          };

      await generateCotizacionPDF({
        venue: currentVenue,
        event: editData || event,
        desglose,
        ivaEnabled,
        folio,
        isPremium: isPremiumCotizaciones,
        cotizacionConfig: resolvedConfig,
      });

      // Append to cotización history
      const historyEntry = {
        generatedAt: Timestamp.now(),
        generatedBy: user?.uid || "",
        generatedByName: userInfo?.name || "",
        folio,
        sent: false,
        sentAt: null,
        sentBy: null,
      };
      const eventDocRef = doc(db, "venues", venueId, "calendars", "default", "events", eventId);
      await updateDoc(eventDocRef, {
        cotizacionHistory: arrayUnion(historyEntry),
      });

      const desgloseForLog = desglose.map((item) => ({
        concepto: item.concept || "-",
        unidades: item.units,
        precioUnitario: item.unitPrice,
        subtotal: Number(item.units || 0) * Number(item.unitPrice || 0),
      }));
      const logTotal = desglose.reduce(
        (acc, item) => acc + Number(item.units || 0) * Number(item.unitPrice || 0),
        0
      );

      logBitacora(venueId, {
        action: BITACORA_ACTIONS.COTIZACION_GENERATE,
        entity: "cotizacion",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: {
          folio,
          desglose: desgloseForLog,
          subtotal: logTotal,
          iva: ivaEnabled ? logTotal * 0.16 : 0,
          total: ivaEnabled ? logTotal * 1.16 : logTotal,
        },
      }, userInfo, user);

      showAlert("Cotización PDF generada", "success");
    } catch (err) {
      showAlert(`Error al generar PDF: ${err.message}`, "error");
    } finally {
      setExportingPDF(false);
    }
  };

  const handleToggleCotizacionSent = async (idx) => {
    if (!venueId || !eventId) return;
    const history = [...(event?.cotizacionHistory || [])];
    if (!history[idx]) return;
    const wasSent = history[idx].sent;
    history[idx] = {
      ...history[idx],
      sent: !wasSent,
      sentAt: !wasSent ? Timestamp.now() : null,
      sentBy: !wasSent ? user?.uid || "" : null,
    };
    try {
      const eventDocRef = doc(db, "venues", venueId, "calendars", "default", "events", eventId);
      await updateDoc(eventDocRef, { cotizacionHistory: history });
      setEvent((prev) => ({ ...prev, cotizacionHistory: history }));
      setEditData((prev) => ({ ...prev, cotizacionHistory: history }));
      if (!wasSent) {
        logBitacora(venueId, {
          action: BITACORA_ACTIONS.COTIZACION_MARK_SENT,
          entity: "cotizacion",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: { folio: history[idx].folio },
        }, userInfo, user);
      }
    } catch (err) {
      showAlert(`Error: ${err.message}`, "error");
    }
  };

  // Persiste lo que el Balance ya está derivando del desglose: asigna a los
  // proveedores implicados y registra sus costos por pagar. Lo llama el
  // guardado de la cotización y también el botón "Registrar" de la tarjeta de
  // proveedores, que es como un evento viejo se pone al corriente sin que
  // nadie migre nada a mano.
  const aplicarCotizacionAlGasto = async (
    desgloseActual,
    { silencioso = false, soloProveedorId = null } = {}
  ) => {
    if (!venueId || !eventId) return;
    try {
      const resumen = await sincronizarCotizacion({
        venueId,
        eventId,
        desglose: desgloseActual,
        // Completo, no `conceptos` (cotizables): un servicio archivado que
        // sigue en el desglose debe seguir encontrándose, o se borra su
        // costo automático.
        conceptos: conceptosTodos,
        proveedores: venueId === contextVenueId ? contextProveedores : [],
        userId: user?.uid || null,
        soloProveedorId,
      });
      if (!resumen) {
        if (!silencioso) showAlert("No había nada pendiente por registrar.", "success");
        return;
      }
      setEvent((prev) => ({ ...prev, proveedoresAsignados: resumen.proveedoresAsignados }));
      setEditData((prev) => ({ ...prev, proveedoresAsignados: resumen.proveedoresAsignados }));
      const partes = [];
      if (resumen.asignados > 0) {
        partes.push(
          resumen.nombres.length > 0
            ? `${resumen.nombres.join(", ")} asignado${resumen.asignados === 1 ? "" : "s"} al evento`
            : `${resumen.asignados} proveedor${resumen.asignados === 1 ? "" : "es"} asignado${resumen.asignados === 1 ? "" : "s"}`
        );
      }
      if (resumen.creados > 0) {
        partes.push(
          `$${resumen.montoCreado.toLocaleString("es-MX")} en costos registrados por pagar`
        );
      }
      if (resumen.actualizados > 0) partes.push("costos revaluados");
      if (resumen.borrados > 0 || resumen.desasignados > 0) {
        partes.push("se retiró lo que ya no respalda ninguna línea");
      }
      if (partes.length > 0) showAlert(partes.join(" · "), "success");
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_EDIT_DESGLOSE,
        entity: "event",
        entityId: eventId,
        entityName: event?.nombre || "",
        details: {
          sincronizacion: {
            asignados: resumen.asignados,
            desasignados: resumen.desasignados,
            costosCreados: resumen.creados,
            costosActualizados: resumen.actualizados,
            costosBorrados: resumen.borrados,
            montoCreado: resumen.montoCreado,
          },
        },
      }, userInfo, user);
    } catch (err) {
      console.error("Sincronización cotización → proveedores/costos:", err);
      showAlert(
        "No se pudieron registrar los costos de los proveedores de la cotización. Captúralos en la sección de costos.",
        "error"
      );
    }
  };

  // Lo que aprietan los botones de la tarjeta de proveedores: sin argumento,
  // todos los pendientes; con un id, solo ese proveedor (su propia fila).
  const registrarPendienteCotizacion = (soloProveedorId = null) =>
    aplicarCotizacionAlGasto(editData?.desglose || [], { soloProveedorId });

  const saveDesgloseToDB = async (newDesglose) => {
    if (!venueId || !eventId) return;
    const eventDocRef = doc(
      db,
      "venues",
      venueId,
      "calendars",
      "default",
      "events",
      eventId
    );
    const update = { desglose: newDesglose };
    // La cotización manda: al editarla, el precio acordado se sincroniza con
    // su total. Si el desglose queda vacío o en cero, el precio se conserva.
    const nuevoTotal = totalDesglose(newDesglose, editData?.ivaEnabled);
    if (newDesglose.length > 0 && nuevoTotal > 0) {
      update.precioAcordado = nuevoTotal;
    }
    await updateDoc(eventDocRef, update);
    setEvent((prev) => ({ ...prev, ...update }));
    setEditData((prev) => ({ ...prev, ...update }));
    logBitacora(venueId, {
      action: BITACORA_ACTIONS.EVENT_EDIT_DESGLOSE,
      entity: "event",
      entityId: eventId,
      entityName: event?.nombre || "",
      details: update.precioAcordado != null ? { precioAcordado: update.precioAcordado } : {},
    }, userInfo, user);

    // La cotización manda también del lado del gasto (ver
    // utils/cotizacionProveedores.js). Va DESPUÉS del guardado y en su propio
    // try: si esto falla, la cotización ya quedó guardada.
    await aplicarCotizacionAlGasto(newDesglose, { silencioso: true });
  };

  const handleQuickAddMetodo = async (nombre, target) => {
    if (!nombre.trim()) return;
    const exists = metodosPago.some(
      (m) => m.nombre.toLowerCase() === nombre.trim().toLowerCase()
    );
    if (exists) return;
    const col = collection(db, "venues", venueId, "metodosPago");
    const docRef = await addDoc(col, { nombre: nombre.trim() });
    logBitacora(venueId, {
      action: BITACORA_ACTIONS.METODO_PAGO_CREATE,
      entity: "metodo_pago",
      entityId: docRef.id,
      entityName: nombre.trim(),
      details: {},
    }, userInfo, user);
    const newM = { id: docRef.id, nombre: nombre.trim() };
    setMetodosPago((prev) =>
      [...prev, newM].sort((a, b) =>
        a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" })
      )
    );
    if (target === "pago") {
      setNewPago((p) => ({ ...p, metodo: nombre.trim() }));
      setQuickMetodo("");
      setShowQuickAddMetodo(false);
    } else {
      setNewRefund((r) => ({ ...r, metodo: nombre.trim() }));
      setQuickMetodoRefund("");
      setShowQuickAddMetodoRefund(false);
    }
    showAlert("Método de pago creado y seleccionado.", "success");
  };

  // Show error via alert as a side effect, not during render
  useEffect(() => {
    if (error) showAlert(error, "error");
  }, [error]);

  if (loading) {
    return <div className="p-8 text-center">Cargando evento...</div>;
  }
  if (error) {
    return null;
  }
  if (!event || !editData) {
    return (
      <div className="p-8 text-center text-zinc-500">
        No se encontró el evento.
      </div>
    );
  }

  const eventType = event.eventType;
  const config = EVENT_TYPE_CONFIG[eventType] || EVENT_TYPE_CONFIG.event;
  const fields = getEventFields(eventType);

  const allTimeFields = [
    "startTime",
    "inicioMontaje",
    "inicioEvento",
    "finEvento",
    "finMontaje",
    "endTime",
  ].filter((key) => fields.includes(key));
  const basicTimeFields = ["startTime", "endTime"].filter((key) => fields.includes(key));
  const advancedTimeFields = ["inicioMontaje", "inicioEvento", "finEvento", "finMontaje"].filter((key) => fields.includes(key));

  const clientFields = [
    "contacto",
    "cliente",
    "apellido",
    "clientType",
    "nombreEmpresa",
    "correo",
    "telefono",
    "rfc",
  ].filter((key) => fields.includes(key));

  const paymentFields = ["precioAcordado"].filter((key) =>
    fields.includes(key)
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => {
          const from = location.state?.from;
          if (from === "leads-calendario") {
            navigate("/dashboard/leads", { state: { tab: "calendario" } });
          } else if (from === "proveedor" && location.state?.proveedorId) {
            const returnTab = location.state.proveedorTab || "info";
            navigate(
              `/dashboard/proveedores/${location.state.proveedorId}?tab=${encodeURIComponent(returnTab)}`
            );
          } else if (from === "cuentas-depositos") {
            navigate("/dashboard/cuentas/depositos");
          } else {
            navigate(-1);
          }
        }}
        className="inline-flex items-center gap-1 text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition mb-4"
      >
        ←{" "}
        {location.state?.from === "leads-calendario"
          ? "Volver al calendario"
          : location.state?.from === "proveedor"
          ? "Volver al proveedor"
          : location.state?.from === "cuentas-depositos"
          ? "Volver a depósitos en garantía"
          : "Volver"}
      </button>

      {/* Header card */}
      <div className={`rounded-xl border bg-white dark:bg-zinc-800/50 p-6 shadow-sm mb-6 ${isCancelled ? "border-red-200 dark:border-red-800/40 opacity-75" : "border-zinc-200 dark:border-zinc-700/60"}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge color={config.badge}>{config.label}</Badge>
              {isCancelled && <Badge color="red">Cancelado</Badge>}
            </div>
            {isEditing ? (
              <input
                value={editData.nombre || ""}
                onChange={(e) =>
                  setEditData((prev) => ({ ...prev, nombre: e.target.value }))
                }
                placeholder="Nombre del evento"
                className="w-full bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded-lg px-3 py-1.5 text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
              />
            ) : (
              <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white" style={{ hyphens: "auto", overflowWrap: "break-word" }} lang="es">
                {editData.nombre || <span className="italic text-zinc-400">Sin nombre</span>}
              </h1>
            )}
          </div>
          {!isCancelled && !soloLectura && (
            <button
              type="button"
              onClick={() => setIsEditing((v) => !v)}
              className="shrink-0 rounded-lg p-2 transition hover:bg-zinc-100 dark:hover:bg-zinc-700/60"
              title={isEditing ? "Cancelar edición" : "Editar"}
            >
              {isEditing ? (
                <XMarkIcon className="h-5 w-5 text-zinc-500" />
              ) : (
                <PencilSquareIcon className="h-5 w-5 text-zinc-500" />
              )}
            </button>
          )}
        </div>

        {/* Stats for event type */}
        {eventType === "event" && (
          <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-zinc-100 dark:border-zinc-700/40">
            <div className="flex items-center gap-2">
              <UserGroupIcon
                className="h-5 w-5"
                style={{ color: ACCENT }}
              />
              <span className="text-sm text-zinc-500"># de personas:</span>
              {isEditing ? (
                <Input
                  type="number"
                  min="0"
                  value={editData.numPeople || ""}
                  onChange={(e) =>
                    setEditData((prev) => ({
                      ...prev,
                      numPeople: e.target.value,
                    }))
                  }
                  className="w-20"
                />
              ) : (
                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                  {editData.numPeople || "-"}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <UserIcon className="h-5 w-5" style={{ color: ACCENT }} />
              <span className="text-sm text-zinc-500">Vendedor:</span>
              {isEditing ? (
                <Input
                  type="text"
                  value={editData.vendedor || ""}
                  onChange={(e) =>
                    setEditData((prev) => ({
                      ...prev,
                      vendedor: e.target.value,
                    }))
                  }
                  className="w-32"
                />
              ) : (
                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                  {editData.vendedor || "-"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Quick financial summary — variante para cancelados: "Pendiente"
            es saldo por cobrar y no aplica; lo relevante es el reembolso. */}
        {eventType === "event" && !isViewer && isCancelled && totalPagado > 0 && (
          <div className="flex flex-wrap gap-6 mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-700/40">
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="h-5 w-5" style={{ color: ACCENT }} />
              <span className="text-sm text-zinc-500">Pagado:</span>
              <span className="text-sm font-medium text-zinc-900 dark:text-white">
                ${totalPagado.toLocaleString("es-MX")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-500">Reembolsado:</span>
              <span className="text-sm font-medium text-red-600">
                ${totalReembolsado.toLocaleString("es-MX")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-500">Sin reembolsar:</span>
              <span className={`text-sm font-semibold ${pendienteReembolso > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                ${pendienteReembolso.toLocaleString("es-MX")}
              </span>
            </div>
          </div>
        )}
        {eventType === "event" && !isViewer && !isCancelled && (valorEvento > 0 || isEditing) && (
          <div className="flex flex-wrap gap-6 mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-700/40">
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="h-5 w-5" style={{ color: ACCENT }} />
              <span className="text-sm text-zinc-500">
                {isEditing ? "Precio acordado:" : "Valor del evento:"}
              </span>
              {isEditing ? (
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none text-sm">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.,]*"
                    placeholder="0"
                    className="w-36 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-6 pr-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                    value={editData?.precioAcordado ? fmtMXN(String(editData.precioAcordado)) : ""}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1").replace(/(\.\d{2})\d+/, "$1");
                      handleChange("precioAcordado", raw);
                    }}
                  />
                </div>
              ) : (
                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                  ${valorEvento.toLocaleString("es-MX")}
                  {valorVieneDeCotizacion(editData) && (
                    <span className="ml-1 text-xs font-normal text-zinc-400">
                      (según cotización)
                    </span>
                  )}
                </span>
              )}
            </div>
            {!isEditing && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-500">Pagado:</span>
                  <span className="text-sm font-medium text-zinc-900 dark:text-white">
                    ${Number(event.totalPagado || 0).toLocaleString("es-MX")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-500">Pendiente:</span>
                  <span className={`text-sm font-semibold ${balance > 0 ? "text-red-600" : "text-green-600"}`}>
                    ${balance.toLocaleString("es-MX")}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Cliente del evento (FRA-354). Una sola lista: tarjetas de los
            clientes del directorio que participan, con rol de texto libre.
            La principal carga con el dinero y lleva sus acciones (cambiar,
            desligar). Abajo, los campos sueltos del evento son los "datos
            para la cotización": lo que sale en el PDF, congelado al evento;
            si difieren del cliente, se dice y se ofrece copiarlos. */}
        {clientFields.length > 0 && (
          <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-700/40">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <UserIcon className="h-4 w-4" style={{ color: ACCENT }} />
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                {eventType === "event" ? "Cliente" : "Contacto"}
              </span>
              {eventType === "event" && !isViewer && venueId === contextVenueId && !isEditing && !soloLectura && (
                <span className="ml-auto inline-flex items-center gap-3 text-xs">
                  {event.clienteId ? (
                    <button
                      type="button"
                      onClick={() => setAgregandoCliente(true)}
                      className="inline-flex items-center gap-1 font-semibold hover:underline"
                      style={{ color: ACCENT }}
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      Agregar otro cliente
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setLigandoCliente(true)}
                      className="font-semibold hover:underline"
                      style={{ color: ACCENT }}
                    >
                      Ligar o dar de alta al cliente
                    </button>
                  )}
                </span>
              )}
            </div>
            <ClienteEventoDialog
              open={ligandoCliente}
              datosDelEvento={clienteDesdeEvento(event)}
              excluir={[event.clienteId, ...clientesAdicionales.map((x) => x.clienteId)]}
              titulo={event.clienteId ? "Cambiar el cliente del evento" : "Cliente del evento"}
              subtitulo={
                event.clienteId
                  ? `Hoy está ligado a ${clienteDelEvento ? nombreCliente(clienteDelEvento) : "otro cliente"}. Elige quién lo reemplaza.`
                  : "Búscalo en el directorio o dalo de alta"
              }
              onClose={() => setLigandoCliente(false)}
              onLigar={async (clienteId, { creada, cliente } = {}) => {
                setLigandoCliente(false);
                if (creada) {
                  await ligarClienteCreado(clienteId);
                  showAlert("Cliente creado y ligado al evento", "success");
                } else {
                  await ligarCliente(cliente || { id: clienteId });
                }
              }}
            />
            <ClienteEventoDialog
              open={agregandoCliente}
              datosDelEvento={{}}
              excluir={[event.clienteId, ...clientesAdicionales.map((x) => x.clienteId)]}
              titulo="Agregar otro cliente al evento"
              subtitulo="Quién más participa: el novio y la novia, la productora y quien paga…"
              textoCrear="Crear y agregar al evento"
              onClose={() => setAgregandoCliente(false)}
              onLigar={(clienteId, { creada, cliente } = {}) =>
                agregarClienteAdicional(clienteId, { creada, cliente })
              }
            />

            {eventType === "event" && !isViewer && venueId === contextVenueId &&
              (event.clienteId || clientesAdicionales.length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {[
                  ...(event.clienteId ? [{ clienteId: event.clienteId, rol: event.clienteRol || "", principal: true }] : []),
                  ...clientesAdicionales.map((x) => ({ ...x, principal: false })),
                ].map((entrada) => {
                  const ficha = fichaPorId(entrada.clienteId);
                  const puedeEditar = !isEditing && !soloLectura && !guardandoClientes;
                  return (
                    <div
                      key={entrada.clienteId}
                      className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50/60 dark:bg-zinc-800/30 px-3 py-2.5"
                    >
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {ficha ? (
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/dashboard/clientes/${entrada.clienteId}`, {
                                    state: { from: "evento", eventId, eventTab },
                                  })
                                }
                                className="text-sm font-medium text-zinc-900 dark:text-white hover:underline truncate"
                              >
                                {nombreCliente(ficha)}
                              </button>
                            ) : (
                              <span className="text-sm text-zinc-400 italic">Cliente no disponible</span>
                            )}
                            {entrada.principal && (
                              <span
                                className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400"
                                title="Su historial carga con el valor y los pagos de este evento"
                              >
                                Principal
                              </span>
                            )}
                          </div>
                          {ficha && (
                            <div className="text-xs text-zinc-500 truncate">
                              {[contactoCliente(ficha), ficha.correo, ficha.telefono].filter(Boolean).join(" · ") || "Sin datos de contacto"}
                            </div>
                          )}
                          <div className="mt-1 flex items-baseline gap-1.5 text-xs">
                            <span className="text-zinc-500 dark:text-zinc-400 shrink-0">Rol:</span>
                            {guardandoClientes && (
                              <span className="order-last text-zinc-400 italic">Guardando…</span>
                            )}
                            <span className="text-zinc-900 dark:text-white min-w-0">
                              <EditableCell
                                value={entrada.rol || ""}
                                disabled={!puedeEditar}
                                formatDisplay={(v) => v || (puedeEditar ? "agregar rol" : "")}
                                onSave={(v) =>
                                  entrada.principal
                                    ? cambiarRolPrincipal(v)
                                    : cambiarRolAdicional(entrada.clienteId, v)
                                }
                              />
                            </span>
                          </div>
                          {entrada.principal && puedeEditar && (
                            <div className="mt-1.5 flex items-center gap-3 text-xs">
                              <button
                                type="button"
                                onClick={() => setLigandoCliente(true)}
                                className="font-medium text-zinc-500 hover:text-zinc-700 hover:underline"
                              >
                                Cambiar
                              </button>
                              <button type="button" onClick={desligarCliente} className="text-zinc-400 hover:text-zinc-600 hover:underline">
                                Desligar
                              </button>
                            </div>
                          )}
                        </div>
                        {!entrada.principal && puedeEditar && (
                          <button
                            type="button"
                            onClick={() => quitarClienteAdicional(entrada.clienteId)}
                            className="shrink-0 rounded p-1 text-zinc-400 hover:text-red-500 hover:bg-red-50"
                            title="Quitar del evento"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {eventType === "event" && event.clienteId && (
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                  Datos para la cotización
                </span>
                {datosDifierenDelCliente && (
                  <span className="text-[11px] text-amber-600" title="Lo que sale en el PDF no coincide con lo que dice el cliente en el directorio">
                    · difieren del cliente
                  </span>
                )}
                {datosDifierenDelCliente && !isEditing && !soloLectura && clienteDelEvento && (
                  <button
                    type="button"
                    onClick={() => ligarCliente(clienteDelEvento, { rellenar: true })}
                    className="text-[11px] font-semibold hover:underline"
                    style={{ color: ACCENT }}
                  >
                    Usar los de {nombreCliente(clienteDelEvento)}
                  </button>
                )}
              </div>
            )}
            {/* En lectura NO es rejilla: era de 4 columnas fijas, así que
                "PRODUCTORA DE EVENTOS CLASS" se recortaba a la mitad con
                media tarjeta vacía a la derecha. Envuelto, cada dato ocupa
                lo que mide y la fila usa el ancho que hay. */}
            <div className={isEditing
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              : "flex flex-wrap gap-x-6 gap-y-1"}>
              {clientFields.map((key) => {
                if (key === "nombreEmpresa" && editData.clientType !== "moral") {
                  return null;
                }
                // Un campo vacío no se pinta: tres renglones diciendo
                // "Correo: -  Teléfono: -  RFC: -" ocupan más que la
                // información que sí hay y no dicen nada. Lo que falta se
                // dice una sola vez, abajo.
                if (!isEditing && key !== "clientType" && !event[key]) {
                  return null;
                }
                if (!isEditing) {
                  return (
                    <div key={key} className="flex items-baseline gap-1.5 text-sm min-w-0">
                      <span className="text-zinc-500 dark:text-zinc-400 shrink-0">
                        {getFieldLabel(key, eventType)}:
                      </span>
                      <span className="font-medium text-zinc-900 dark:text-white break-words">
                        {key === "clientType"
                          ? editData.clientType === "moral"
                            ? "Persona moral"
                            : "Persona física"
                          : displayValue(key, event[key])}
                      </span>
                    </div>
                  );
                }
                return (
                  <div key={key}>
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-0.5">
                      {getFieldLabel(key, eventType)}
                    </div>
                    {isEditing ? (
                      key === "clientType" ? (
                        <select
                          className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-sm text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                          value={editData.clientType || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              clientType: e.target.value,
                            }))
                          }
                        >
                          <option value="fisica">Persona física</option>
                          <option value="moral">Persona moral</option>
                        </select>
                      ) : (
                        <Input
                          type={key === "correo" ? "email" : "text"}
                          value={editData[key] || ""}
                          onChange={(e) => handleChange(key, e.target.value)}
                          className="w-full"
                        />
                      )
                    ) : (
                      <div className="text-sm font-medium text-zinc-900 dark:text-white">
                        {key === "clientType"
                          ? editData.clientType === "moral"
                            ? "Persona moral"
                            : "Persona física"
                          : displayValue(key, event[key])}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Aviso en eventos cancelados con pagos sin reembolsar: los pagos
          siguen contando como ingresos en Cuentas hasta que se registre el
          reembolso, y esa sección vive al fondo de la pestaña Balance. */}
      {isCancelled && !isViewer && paymentFields.length > 0 && pendienteReembolso > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 px-4 py-3">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
          <p className="min-w-[240px] flex-1 text-sm text-amber-800 dark:text-amber-300">
            Este evento cancelado tiene{" "}
            <span className="font-semibold">
              ${pendienteReembolso.toLocaleString("es-MX")}
            </span>{" "}
            en pagos sin reembolsar. Los pagos siguen contando como ingresos en
            Cuentas; si devuelves dinero al cliente, registra el reembolso.
          </p>
          {!soloLectura && (
          <button
            type="button"
            onClick={() => {
              setEventTab("balance");
              setScrollToReembolsos(true);
            }}
            className="shrink-0 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-white dark:bg-zinc-800 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 transition hover:bg-amber-100 dark:hover:bg-amber-500/20"
          >
            Registrar reembolso
          </button>
          )}
        </div>
      )}

      {/* Main content form */}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (clashingEvents.length > 0) {
            setShowClashConfirm(true);
          } else {
            const ok = await handleSave();
            if (ok) setIsEditing(false);
          }
        }}
      >
        {/* Action buttons — sticky bar */}
        {isEditing && (
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 mb-6 py-3 px-4 -mx-1 rounded-xl bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm border border-zinc-200 dark:border-zinc-700/60 shadow-sm">
            {TIPOS_BORRABLES.includes(event?.eventType) && !soloLectura && (
              <Button type="button" color="red" onClick={() => setConfirmarBorrado(true)} disabled={saving}>
                <TrashOutlineIcon className="h-4 w-4" />
                Eliminar
              </Button>
            )}
            <div className="flex-1" />
            <Button
              type="button"
              outline
              onClick={() => {
                setEditData(event);
                setIsEditing(false);
              }}
              disabled={saving}
            >
              Cancelar
            </Button>
            <button
              type="submit"
              disabled={saving || hasDateErrors}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: ACCENT }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled)
                  e.currentTarget.style.backgroundColor = "#164026";
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled)
                  e.currentTarget.style.backgroundColor = ACCENT;
              }}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        )}

        {/* Tabs — mismo patrón que el Directorio (DirectorioPage.js): franja
            de ancho completo, cada pestaña centrada, y la activa marcada con
            el verde de marca abajo.
            SIN CONTEOS, a diferencia del Directorio: allá cada pestaña ES una
            lista y el número contesta "cuántos hay". Aquí no son listas, así
            que un "2" junto a Balance no significa nada para quien lo lee. */}
        <div className="mb-6 flex border-b border-zinc-200 dark:border-zinc-700/60">
          {[
            { id: "general", label: "General" },
            ...(isViewer ? [] : [{ id: "cotizacion", label: "Cotización" }]),
            // Un viewer no ve dinero, pero sí quién participa: Operación es
            // su pestaña; Balance no se le ofrece.
            { id: "operacion", label: "Operación" },
            ...(isViewer ? [] : [{ id: "balance", label: "Balance" }]),
            { id: "seguimientos", label: "Seguimientos" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setEventTab(t.id)}
              className={`flex-1 min-w-0 truncate py-3 text-xs sm:text-sm font-semibold text-center transition border-b-2 ${
                eventTab === t.id
                  ? "text-zinc-900 dark:text-white"
                  : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 border-transparent"
              }`}
              style={eventTab === t.id ? { borderBottomColor: ACCENT } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Espacios: en qué salón ocurre. La ficha no lo decía y el único
            lugar para asignarlo era el diálogo del calendario — justo donde
            NO cae quien convierte un lead. */}
        {eventTab === "general" && espaciosActivos.length > 0 && (
          <Section icon={Squares2X2Icon} title="Espacios" className="mb-6">
            {espaciosEvento.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {espaciosEvento.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
                    style={{ backgroundColor: ACCENT + "1a", color: ACCENT }}
                  >
                    {espaciosPorId[id]?.nombre || id}
                  </span>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-500/20 dark:bg-amber-500/10">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-400">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  Este evento no tiene espacio asignado
                </div>
                <p className="mt-1 ml-6 text-xs text-amber-700/90 dark:text-amber-400/80">
                  Mientras no lo tenga, bloquea TODOS los espacios del venue —
                  en tu calendario y en la disponibilidad de tu página pública.
                </p>
              </div>
            )}
            {!soloLectura && (
              <div className="mt-3">
                <EspaciosPicker
                  opciones={opcionesEspaciosEvento}
                  value={espaciosEdit}
                  onChange={setEspaciosEdit}
                  size="sm"
                />
                <div className="mt-2 flex items-center gap-2">
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    {espaciosEdit.length === 0
                      ? "Sin selección, el evento ocupa todo el venue."
                      : "Al guardar se recalcula qué bloquea con la configuración actual."}
                  </p>
                  {espaciosCambiaron && (
                    <button
                      type="button"
                      onClick={handleSaveEspacios}
                      disabled={savingEspacios}
                      className="ml-auto shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: ACCENT }}
                    >
                      {savingEspacios ? "Guardando..." : "Guardar espacios"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Horarios card */}
        {eventTab === "general" && allTimeFields.length > 0 && (
          <Section icon={ClockIcon} title="Horarios" className="mb-6">
            <div className="space-y-4">
              {(showAdvancedDates ? allTimeFields : basicTimeFields).map((key) => (
                <div key={key}>
                  <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
                    {getFieldLabel(key, eventType)}
                  </div>
                  {isEditing ? (
                    <DateTimeDropdowns
                      value={formatDateTimeLocal(editData[key], timeZone)}
                      onChange={(v) => handleChange(key, v)}
                    />
                  ) : (
                    <div className="text-sm font-medium text-zinc-900 dark:text-white">
                      {displayValue(key, event[key])}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Date order validation */}
            {isEditing && dateWarnings.length > 0 && (
              <div className="mt-3 rounded-lg border border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20 px-3 py-2 space-y-1">
                {dateWarnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
                    {w}
                  </div>
                ))}
              </div>
            )}
            {advancedTimeFields.length > 0 && (
              <button
                type="button"
                onClick={() => setShowAdvancedDates((v) => !v)}
                className="mt-3 text-xs font-medium transition"
                style={{ color: ACCENT }}
              >
                {showAdvancedDates
                  ? "Ocultar fechas avanzadas"
                  : isEditing
                    ? "Editar fechas avanzadas"
                    : "Ver fechas avanzadas"}
              </button>
            )}
            {/* Clash warning */}
            {isEditing && clashingEvents.length > 0 && (
              <div className="mt-4 rounded-lg border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3">
                <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">
                  Las fechas se cruzan con:
                </div>
                <ul className="list-disc list-inside text-xs text-amber-600 dark:text-amber-400 space-y-0.5">
                  {clashingEvents.map((ce) => {
                    const typeLabel = EVENT_TYPE_CONFIG[ce.eventType]?.label;
                    return (
                      <li key={ce.id}>
                        {typeLabel && <span className="font-semibold">{typeLabel}:</span>}{" "}
                        {ce.nombre || "Sin nombre"}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </Section>
        )}

        {/* Cotización card — full width (hidden for viewers) */}
        {eventTab === "cotizacion" && paymentFields.length > 0 && !isViewer && (
          <Section
            icon={DocumentTextIcon}
            title="Cotización"
            className="mb-6"
            action={
              Array.isArray(desglose) && desglose.length > 0 ? (
                <div className="inline-flex items-center gap-2">
                  <span className="group relative inline-flex">
                    <QuestionMarkCircleIcon className="h-4 w-4 text-zinc-400 cursor-help" />
                    <span className="pointer-events-none absolute right-0 top-6 z-10 w-56 rounded-lg bg-zinc-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
                      Personaliza colores, logo y más en la pestaña de Configuración → Cotizaciones.
                    </span>
                  </span>
                  {!soloLectura && (
                  <button
                    type="button"
                    onClick={handleExportPDF}
                    disabled={exportingPDF}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition disabled:opacity-50"
                    style={{ backgroundColor: ACCENT }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#164026")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = ACCENT)}
                  >
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                    {exportingPDF ? "Generando..." : "Exportar PDF"}
                  </button>
                  )}
                </div>
              ) : null
            }
          >
            <div className="mb-6">
              <CotizadorDesglose
                venueId={venueId}
                desglose={desglose}
                ivaEnabled={ivaEnabled}
                onIvaChange={saveIvaEnabled}
                onDesgloseChange={(nuevo) => {
                  setEditData((prev) => ({ ...prev, desglose: nuevo }));
                  saveDesgloseToDB(nuevo);
                }}
                conceptos={conceptos}
                conceptosTodos={conceptosTodos}
                conceptosLoading={loadingConceptos}
                precioAcordado={Number(editData.precioAcordado || 0)}
                puedeCrearServicio={venueId === contextVenueId && !soloLectura}
                titulo="Desglose del precio acordado"
                soloLectura={soloLectura}
              />
            </div>

            {/* Cotización history */}
            {cotizacionHistory.length > 0 && (
              <div className="mb-6">
                <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-3">
                  Historial de cotizaciones
                </div>
                <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr style={{ backgroundColor: ACCENT + "0d" }}>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Folio</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Fecha</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Generada por</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                      {cotizacionHistory.map((entry, idx) => {
                        const genDate = entry.generatedAt?.seconds
                          ? new Date(entry.generatedAt.seconds * 1000).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "-";
                        return (
                          <tr key={idx}>
                            <td className="px-3 py-2 font-medium text-zinc-900 dark:text-white">{entry.folio || `COT-${String(idx + 1).padStart(4, "0")}`}</td>
                            <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">{genDate}</td>
                            <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">{entry.generatedByName || "-"}</td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                disabled={soloLectura}
                                onClick={() => handleToggleCotizacionSent(idx)}
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition disabled:cursor-default ${
                                  entry.sent
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                    : "bg-zinc-100 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400"
                                }`}
                                title={soloLectura ? undefined : entry.sent ? "Clic para marcar como no enviada" : "Clic para marcar como enviada"}
                              >
                                {entry.sent ? "Enviada" : "No enviada"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Precio acordado */}
            <div className="pt-4 border-t border-zinc-100 dark:border-zinc-700/40">
              {paymentFields.map((key) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-zinc-500">
                    {getFieldLabel(key, eventType)}:
                  </span>
                  {editingPrecioAcordado ? (
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none text-sm">
                          $
                        </span>
                        <input
                          type="text"
                          className="w-36 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-7 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                          value={precioAcordadoInput ? fmtMXN(precioAcordadoInput) : ""}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1").replace(/(\.\d{2})\d+/, "$1");
                            setPrecioAcordadoInput(raw);
                          }}
                          inputMode="decimal"
                          pattern="[0-9.,]*"
                          placeholder="0"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !precioAcordadoInput ||
                            isNaN(Number(precioAcordadoInput))
                          ) {
                            showAlert(
                              "Por favor ingresa un precio válido.",
                              "error"
                            );
                            return;
                          }
                          const newPrecio = Number(precioAcordadoInput);
                          if (!venueId || !eventId) return;
                          const eventDocRef = doc(
                            db,
                            "venues",
                            venueId,
                            "calendars",
                            "default",
                            "events",
                            eventId
                          );
                          await updateDoc(eventDocRef, {
                            precioAcordado: newPrecio,
                          });
                          logBitacora(venueId, {
                            action: BITACORA_ACTIONS.EVENT_EDIT_PRECIO,
                            entity: "event",
                            entityId: eventId,
                            entityName: event?.nombre || "",
                            details: { precio: newPrecio },
                          }, userInfo, user);
                          setEvent((prev) => ({
                            ...prev,
                            precioAcordado: newPrecio,
                          }));
                          setEditData((prev) => ({
                            ...prev,
                            precioAcordado: newPrecio,
                          }));
                          setEditingPrecioAcordado(false);
                        }}
                        className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition"
                        style={{ backgroundColor: ACCENT }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#164026")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = ACCENT)
                        }
                      >
                        Guardar
                      </button>
                      <Button
                        type="button"
                        outline
                        onClick={() => setEditingPrecioAcordado(false)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-zinc-900 dark:text-white">
                        {displayValue(key, event[key])}
                      </span>
                      {!soloLectura && (
                      <button
                        type="button"
                        onClick={() => {
                          setPrecioAcordadoInput(
                            event[key] ? String(event[key]) : ""
                          );
                          setEditingPrecioAcordado(true);
                        }}
                        className="rounded-lg p-1 transition hover:bg-zinc-100 dark:hover:bg-zinc-700/60"
                        title="Editar precio"
                      >
                        <PencilSquareIcon className="h-4 w-4 text-zinc-400" />
                      </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Pagos card — full width (hidden for viewers) */}
        {/* Balance: resumen de utilidad, luego Cobrado, Costos y Depósitos
            (y Reembolsos si el evento está cancelado). Pagos y Depósitos ya
            existían como tabs propios; aquí quedan bajo el mismo techo que
            los costos, con la utilidad del evento coronándolos. */}
        {eventTab === "balance" && !isViewer && (
          <div className="mb-6 space-y-4">
            {paymentFields.length > 0 && (
            <Section icon={CurrencyDollarIcon} title="Balance del evento">
              {/* En un evento cancelado, valor y utilidad dejan de aplicar;
                  lo que importa es cuánto entró, cuánto se devolvió y cuánto
                  sigue contando como ingreso en Cuentas. */}
              {isCancelled ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3">
                      <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                        Pagado
                      </div>
                      <div className="mt-1 text-xl font-bold text-zinc-900 dark:text-white">
                        ${totalPagado.toLocaleString("es-MX")}
                      </div>
                      {payments.length > 0 && (
                        <div className="mt-0.5 text-xs text-zinc-500">
                          {payments.length} {payments.length === 1 ? "pago" : "pagos"}
                        </div>
                      )}
                    </div>
                    <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3">
                      <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                        Reembolsado
                      </div>
                      <div className="mt-1 text-xl font-bold text-red-600">
                        ${totalReembolsado.toLocaleString("es-MX")}
                      </div>
                      {totalPagado > 0 && (
                        <div className="mt-0.5 text-xs text-zinc-500">
                          {Math.round((totalReembolsado / totalPagado) * 100)}% de lo pagado
                        </div>
                      )}
                    </div>
                    <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3">
                      <div className="flex items-center gap-1 text-xs uppercase tracking-wide font-semibold text-zinc-400">
                        Sin reembolsar
                        <span
                          title="Pagos recibidos menos reembolsos registrados. Mientras sea mayor a cero, sigue contando como ingreso en Cuentas."
                          className="cursor-help"
                        >
                          <QuestionMarkCircleIcon className="h-3.5 w-3.5 text-zinc-300" />
                        </span>
                      </div>
                      <div
                        className={`mt-1 text-xl font-bold ${
                          pendienteReembolso > 0 ? "text-amber-600" : "text-emerald-600"
                        }`}
                      >
                        ${pendienteReembolso.toLocaleString("es-MX")}
                      </div>
                      {pendienteReembolso === 0 && totalPagado > 0 && (
                        <div className="mt-0.5 text-xs text-emerald-600">
                          Reembolso completo
                        </div>
                      )}
                    </div>
                  </div>
                  {balanceEvento.costos > 0 && (
                    <div className="mt-3 text-xs text-zinc-500">
                      Este evento tiene ${balanceEvento.costos.toLocaleString("es-MX")} en
                      costos capturados; el detalle está en la sección de costos.
                    </div>
                  )}
                  {event?.costosLiberadosAlCancelar?.count > 0 && (
                    <div className="mt-3 text-xs text-zinc-500">
                      Al cancelar se liberaron $
                      {Number(event.costosLiberadosAlCancelar.total || 0).toLocaleString("es-MX")} en{" "}
                      {event.costosLiberadosAlCancelar.count === 1
                        ? "un costo por pagar"
                        : `${event.costosLiberadosAlCancelar.count} costos por pagar`}
                      : ya no cuentan como deuda.
                    </div>
                  )}
                  {saldosEvento.length > 0 && (
                    <div className="mt-3 rounded-lg bg-teal-50 dark:bg-teal-500/10 border border-teal-200 dark:border-teal-500/20 px-3 py-2 text-sm text-teal-800 dark:text-teal-300">
                      {saldosEvento.map((c) => {
                        const s = c.saldoAFavor || {};
                        const estado =
                          s.estado === "aplicado"
                            ? `aplicado a ${s.aplicadoEventoNombre || "otro evento"}`
                            : s.estado === "devuelto"
                            ? "devuelto"
                            : "pendiente de resolver en la ficha del proveedor";
                        return (
                          <div key={c.id}>
                            Saldo a favor con{" "}
                            <span className="font-semibold">{c.proveedorNombre || "proveedor"}</span>: $
                            {Number(c.monto || 0).toLocaleString("es-MX")} ({estado}).
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {balanceEvento.porPagar > 0 && (
                    <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-3 py-2 text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                      <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                      Quedan ${balanceEvento.porPagar.toLocaleString("es-MX")} en costos
                      por pagar de este evento cancelado.
                    </div>
                  )}
                </>
              ) : (
                <>
              {/* Los totales se dicen UNA sola vez: aquí. Las secciones de
                  abajo (cobrado, costos, depósitos) ya no repiten los suyos. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3">
                  <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                    Valor del evento
                  </div>
                  <div className="mt-1 text-xl font-bold text-zinc-900 dark:text-white">
                    ${balanceEvento.valor.toLocaleString("es-MX")}
                  </div>
                  {/* "No hay cotización" se contesta con el desglose, NO con
                      valorVieneDeCotizacion: ese helper detecta que el precio
                      acordado quedó DESINCRONIZADO, y como saveDesgloseToDB
                      sincroniza los dos, sería false en todo evento cotizado
                      — el aviso salía siempre, hasta con la cotización hecha. */}
                  {balanceEvento.valor > 0 && desglose.length === 0 && soloLectura && (
                    <div className="mt-0.5 text-xs text-amber-600">Sin cotización</div>
                  )}
                  {balanceEvento.valor > 0 && desglose.length === 0 && !soloLectura && (
                    <button
                      type="button"
                      onClick={() => setEventTab("cotizacion")}
                      className="mt-0.5 text-xs text-amber-600 hover:underline text-left"
                    >
                      Sin cotización · armar el desglose
                    </button>
                  )}
                </div>
                <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3">
                  <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                    Cobrado
                  </div>
                  <div className="mt-1 text-xl font-bold text-emerald-600">
                    ${totalPagado.toLocaleString("es-MX")}
                  </div>
                  {payments.length > 0 && (
                    <div className="mt-0.5 text-xs text-zinc-500">
                      {payments.length} {payments.length === 1 ? "pago" : "pagos"}
                    </div>
                  )}
                </div>
                <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3">
                  <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                    Por cobrar
                  </div>
                  <div className={`mt-1 text-xl font-bold ${balance > 0 ? "text-red-600" : "text-zinc-400"}`}>
                    ${Math.max(0, balance).toLocaleString("es-MX")}
                  </div>
                  {balance <= 0 && valorEvento > 0 && (
                    <div className="mt-0.5 text-xs text-emerald-600">Liquidado</div>
                  )}
                </div>
                <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3">
                  <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                    Costos
                  </div>
                  <div className="mt-1 text-xl font-bold text-red-600">
                    ${balanceEvento.costos.toLocaleString("es-MX")}
                  </div>
                  {/* Lo estimado NO está en el número de arriba: es lo que
                      falta por registrar, no lo que ya se registró. Se
                      enseña aquí para que el aviso de abajo no parezca
                      salido de la nada. */}
                  {hayEstimadoSinRegistrar && (
                    <div className="mt-0.5 text-xs text-amber-600">
                      + ${pendienteCotizacion.montoEstimado.toLocaleString("es-MX")}{" "}
                      sin registrar
                    </div>
                  )}
                  {balanceEvento.porPagar > 0 && (
                    <div className="mt-0.5 text-xs text-amber-600">
                      ${balanceEvento.porPagar.toLocaleString("es-MX")} por pagar
                    </div>
                  )}
                </div>
                <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3">
                  <div className="flex items-center gap-1 text-xs uppercase tracking-wide font-semibold text-zinc-400">
                    Utilidad
                    <span
                      title="Valor del evento (con IVA si la cotización lo incluye) menos sus costos registrados. Es el mismo número que usan los reportes. Lo que la cotización implica y todavía no se registra no cuenta aquí: aparece aparte, para registrarlo. El margen por proveedor, más abajo, sí compara sin IVA."
                      className="cursor-help"
                    >
                      <QuestionMarkCircleIcon className="h-3.5 w-3.5 text-zinc-300" />
                    </span>
                  </div>
                  <div
                    className={`mt-1 text-xl font-bold ${
                      balanceEvento.utilidad < 0 ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    ${balanceEvento.utilidad.toLocaleString("es-MX")}
                  </div>
                  {balanceEvento.margen !== null && (
                    <div className="mt-0.5 text-xs text-zinc-500">
                      Margen {(balanceEvento.margen * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>

              {/* El hueco entre lo que la cotización implica y lo que está
                  registrado. La utilidad de arriba no lo cuenta —los
                  reportes tampoco— así que decirlo aquí, con el botón para
                  cerrarlo, es lo que evita que el margen mienta en silencio. */}
              {hayEstimadoSinRegistrar && !isViewer && (
                <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300 flex flex-wrap items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  <span>
                    Esta cotización implica{" "}
                    <strong>
                      ${pendienteCotizacion.montoEstimado.toLocaleString("es-MX")}
                    </strong>{" "}
                    en costos de proveedor que todavía no están registrados. La
                    utilidad de arriba no los cuenta.
                  </span>
                  {!soloLectura && (
                  <button
                    type="button"
                    onClick={() => registrarPendienteCotizacion()}
                    className="ml-auto shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                    style={{ backgroundColor: ACCENT }}
                  >
                    Registrar
                  </button>
                  )}
                </div>
              )}

              {balanceEvento.valor > 0 && balanceEvento.costos > balanceEvento.valor && (
                <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-3 py-2 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  Los costos de este evento superan su valor.
                </div>
              )}
              {balanceEvento.porPagar > 0 && isPastEvent && (
                <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-3 py-2 text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  El evento ya pasó y quedan costos por pagar.
                </div>
              )}
              {balanceEvento.valor <= 0 && balanceEvento.costos > 0 && (
                <div className="mt-3 text-xs text-zinc-500">
                  Este evento tiene costos pero todavía no tiene valor
                  capturado, así que su margen no se puede calcular.
                </div>
              )}
                </>
              )}
            </Section>
            )}

          </div>
        )}

        {/* Reembolsos section — only for cancelled events (hidden for viewers) */}
        {eventTab === "balance" && isCancelled && paymentFields.length > 0 && !isViewer && (
          <Section
            id="seccion-reembolsos"
            icon={CurrencyDollarIcon}
            title="Reembolsos"
            className="mb-6"
          >
            {/* Quick % buttons + custom % input (solo llenan el formulario
                de alta, así que en solo lectura no aplican) */}
            {totalPagado > 0 && !soloLectura && (
              <div className="mb-4">
                <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">
                  Calcular reembolso por porcentaje
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {[100, 75, 50, 25].map((pct) => {
                    return (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => aplicarPorcentajeReembolso(pct)}
                        className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-700 dark:text-zinc-300 transition hover:bg-zinc-50 dark:hover:bg-zinc-700"
                      >
                        {pct}%
                      </button>
                    );
                  })}
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      defaultValue="0"
                      className="w-16 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-2 pr-6 py-1.5 text-xs text-center focus:outline-none focus:ring-2 focus:ring-[#1b4d2e] [appearance:textfield]"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault();
                      }}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d]/g, "");
                        const pct = Math.min(100, Number(raw) || 0);
                        e.target.value = raw ? String(pct) : "";
                        aplicarPorcentajeReembolso(pct);
                      }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="flex flex-wrap gap-4 mb-4 text-sm">
              <div>
                <span className="text-zinc-500">Total pagado:</span>{" "}
                <span className="font-semibold text-zinc-900 dark:text-white">
                  ${totalPagado.toLocaleString("es-MX")}
                </span>
              </div>
              <div>
                <span className="text-zinc-500">Total reembolsado:</span>{" "}
                <span className="font-semibold text-red-600">
                  ${totalReembolsado.toLocaleString("es-MX")}{totalPagado > 0 && ` (${Math.round(totalReembolsado / totalPagado * 100)}%)`}
                </span>
              </div>
            </div>

            {/* Refunds table */}
            {loadingRefunds ? (
              <p className="text-sm text-zinc-400 italic">Cargando reembolsos...</p>
            ) : eventRefunds.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60 mb-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: "#dc26260d" }}>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Fecha
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Monto
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        %
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Método
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Notas
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 w-20">
                        Comprobante
                      </th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                    {eventRefunds.map((r) => (
                      <tr key={r.id}>
                        <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">
                          {formatCalendarDate(r.fecha, timeZone) || "-"}
                        </td>
                        <td className="px-3 py-2 font-medium text-red-600">
                          <EditableCell
                            value={r.monto}
                            formatDisplay={(v) => `$${Number(v || 0).toLocaleString("es-MX")}`}
                            inputMode="numeric"
                            disabled={soloLectura}
                            onSave={async (val) => {
                              if (!venueId) return;
                              const num = Number(val);
                              if (isNaN(num) || num <= 0) return;
                              const oldMonto = Number(r.monto) || 0;
                              const diff = num - oldMonto;
                              const batch = writeBatch(db);
                              batch.update(fsDoc(db, "venues", venueId, "refunds", r.id), { monto: num });
                              const eventDocRef = fsDoc(db, "venues", venueId, "calendars", "default", "events", eventId);
                              batch.update(eventDocRef, { totalReembolsado: increment(diff) });
                              await batch.commit();
                              logBitacora(venueId, {
                                action: BITACORA_ACTIONS.REFUND_EDIT_AMOUNT,
                                entity: "refund",
                                entityId: eventId,
                                entityName: event?.nombre || "",
                                details: { oldMonto: oldMonto, newMonto: num, refundId: r.id },
                              }, userInfo, user);
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-zinc-500">
                          {totalPagado > 0 ? `${Math.round(Number(r.monto) / totalPagado * 100)}%` : "-"}
                        </td>
                        <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">
                          <select
                            className="w-full rounded border border-transparent bg-transparent py-1 text-sm cursor-pointer hover:border-zinc-300 dark:hover:border-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#1b4d2e] focus:border-zinc-300 disabled:cursor-default disabled:hover:border-transparent disabled:appearance-none"
                            value={r.metodo || ""}
                            disabled={soloLectura}
                            onChange={async (e) => {
                              const val = e.target.value;
                              if (!venueId) return;
                              const refundDocRef = fsDoc(db, "venues", venueId, "refunds", r.id);
                              await updateDoc(refundDocRef, { metodo: val });
                              logBitacora(venueId, {
                                action: BITACORA_ACTIONS.REFUND_EDIT_METHOD,
                                entity: "refund",
                                entityId: eventId,
                                entityName: event?.nombre || "",
                                details: { metodo: val, refundId: r.id },
                              }, userInfo, user);
                            }}
                          >
                            <option value="">-</option>
                            {metodosPago.map((m) => (
                              <option key={m.id} value={m.nombre}>{m.nombre}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300 max-w-[180px]">
                          <EditableCell
                            value={r.notas}
                            disabled={soloLectura}
                            onSave={async (val) => {
                              if (!venueId) return;
                              const refundDocRef = fsDoc(db, "venues", venueId, "refunds", r.id);
                              await updateDoc(refundDocRef, { notas: val });
                              logBitacora(venueId, {
                                action: BITACORA_ACTIONS.REFUND_EDIT_NOTES,
                                entity: "refund",
                                entityId: eventId,
                                entityName: event?.nombre || "",
                                details: { refundId: r.id },
                              }, userInfo, user);
                            }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <ComprobanteCell
                            venueId={venueId}
                            eventId={eventId}
                            eventNombre={event?.nombre}
                            linkedKind="refund"
                            linkedId={r.id}
                            linkedLabel={`Comprobante de reembolso ${formatCalendarDate(r.fecha, timeZone) || ""}`.trim()}
                            archivo={archivosByLinked[`refund:${r.id}`]}
                            currentUser={user}
                            userInfo={userInfo}
                            onChanged={reloadEventArchivos}
                            soloLectura={soloLectura}
                          />
                        </td>
                        <td className="px-3 py-2">
                          {!soloLectura && (
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700 text-xs font-medium"
                            onClick={() => handleRemoveRefund(r.id)}
                          >
                            Eliminar
                          </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-zinc-400 italic mb-4">
                No hay reembolsos registrados.
              </p>
            )}

            {/* Add refund form */}
            {!soloLectura && (
            <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-3">
                Registrar reembolso
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Fecha
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                    value={newRefund.fecha}
                    onChange={(e) =>
                      setNewRefund((r) => ({ ...r, fecha: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Monto
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none text-sm">
                      $
                    </span>
                    <input
                      type="text"
                      className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                      value={newRefund.monto ? fmtMXN(newRefund.monto) : ""}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1").replace(/(\.\d{2})\d+/, "$1");
                        setNewRefund((r) => ({ ...r, monto: raw }));
                      }}
                      inputMode="decimal"
                      pattern="[0-9.,]*"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Método
                  </label>
                  {metodosPago.length > 0 ? (
                    <Listbox
                      value={newRefund.metodo}
                      onChange={(val) => setNewRefund((r) => ({ ...r, metodo: val }))}
                      className="w-full"
                      placeholder="Seleccionar..."
                    >
                      {metodosPago.map((m) => (
                        <ListboxOption key={m.id} value={m.nombre}>
                          {m.nombre}
                        </ListboxOption>
                      ))}
                    </Listbox>
                  ) : (
                    <input
                      type="text"
                      className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                      value={newRefund.metodo}
                      onChange={(e) =>
                        setNewRefund((r) => ({ ...r, metodo: e.target.value }))
                      }
                      placeholder="Efectivo, transferencia..."
                    />
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Notas
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                    value={newRefund.notas}
                    onChange={(e) =>
                      setNewRefund((r) => ({ ...r, notas: e.target.value }))
                    }
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowQuickAddMetodoRefund((v) => !v)}
                className="mt-2 text-xs font-medium transition hover:underline"
                style={{ color: ACCENT }}
              >
                {showQuickAddMetodoRefund ? "Cancelar" : "+ Nuevo método de pago"}
              </button>
              {showQuickAddMetodoRefund && (
                <div className="mt-1.5 flex gap-2 max-w-xs">
                  <input
                    type="text"
                    className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                    value={quickMetodoRefund}
                    onChange={(e) => setQuickMetodoRefund(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleQuickAddMetodo(quickMetodoRefund, "refund"); }}
                    placeholder="Nombre del método"
                  />
                  <button
                    type="button"
                    onClick={() => handleQuickAddMetodo(quickMetodoRefund, "refund")}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white"
                    style={{ backgroundColor: ACCENT }}
                  >
                    Crear
                  </button>
                </div>
              )}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleAddRefund}
                  className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition bg-red-600 hover:bg-red-700"
                >
                  Registrar reembolso
                </button>
              </div>
            </div>
            )}
          </Section>
        )}

        {/* Operación — quién participa: planner y proveedores (absorbe la
            pestaña Proveedores, FRA-357) + los servicios del venue con
            inventario. Lo que cuesta cada quien se administra en Balance. */}
        {eventTab === "operacion" && !isViewer && (
          <p className="mb-4 text-xs text-zinc-400">
            Los costos de proveedores y servicios se administran en{" "}
            <button
              type="button"
              onClick={() => setEventTab("balance")}
              className="font-medium text-zinc-600 dark:text-zinc-300 hover:underline"
            >
              Balance
            </button>
            .
          </p>
        )}
        {eventTab === "operacion" && (
          <div className="mb-6">
            <EventProveedoresSection
              event={event}
              eventId={eventId}
              isViewer={isViewer}
              soloLectura={soloLectura}
              pendiente={pendienteCotizacion}
              onRegistrarPendiente={registrarPendienteCotizacion}
            />
          </div>
        )}

        {eventTab === "operacion" && !isViewer && (
          <div className="mb-6">
            <EventServiciosSection
              venueId={venueId}
              eventId={eventId}
              event={event}
              recursos={recursosDisponibles}
              soloLectura={soloLectura}
            />
          </div>
        )}

        {eventTab === "balance" && !isViewer && (
          <div className="space-y-6 mb-6">
        <p className="text-xs text-zinc-400">
          Quién participa y qué entrega cada proveedor vive en{" "}
          <button
            type="button"
            onClick={() => setEventTab("operacion")}
            className="font-medium text-zinc-600 dark:text-zinc-300 hover:underline"
          >
            Operación
          </button>
          .
        </p>
        <div className="min-w-0">
          <EventCostosSection
            venueId={venueId}
            eventId={eventId}
            eventNombre={event?.nombre || ""}
            costos={eventCostos}
            archivosByLinked={archivosByLinked}
            onArchivosChanged={reloadEventArchivos}
            proveedoresAsignados={event?.proveedoresAsignados || []}
            recursos={recursosDisponibles}
            margenes={margenesProveedor}
            timeZone={timeZone}
            metodosPago={metodosPago}
            soloLectura={soloLectura}
          />
        </div>

        {/* El margen por servicio va pegado a los costos: es lo que explica */}
        {margenesProveedor.length > 0 && (
          <div className="min-w-0">
            <MargenPorRespaldo margenes={margenesProveedor} />
          </div>
        )}

        {paymentFields.length > 0 && (
        <div className="min-w-0">
          <Section
            icon={CurrencyDollarIcon}
            title="Cobrado"
            action={
              !valorEvento ? (
                <Badge color="zinc">Sin cotizar</Badge>
              ) : eventoPagado(editData, totalPagado) ? (
                <Badge color="emerald">Pagado</Badge>
              ) : (
                <Badge color="yellow">Pendiente</Badge>
              )
            }
          >
            {/* Inconsistencias entre cotización, precio y pagos */}
            {inconsistencias.length > 0 && (
              <div className="mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300 space-y-1">
                {inconsistencias.includes("desglose_en_cero") && (
                  <p>
                    La cotización tiene conceptos pero su total es $0 o
                    negativo — revisa el desglose en la pestaña Cotización.
                  </p>
                )}
                {inconsistencias.includes("sobrepago") && (
                  <p>
                    Lo pagado (${totalPagado.toLocaleString("es-MX")}) excede
                    el valor del evento (${valorEvento.toLocaleString("es-MX")})
                    por ${(totalPagado - valorEvento).toLocaleString("es-MX", { maximumFractionDigits: 2 })}.
                  </p>
                )}
                {inconsistencias.includes("pagos_sin_valor") && (
                  <p>
                    Hay pagos registrados pero el evento no tiene valor
                    definido (sin cotización ni precio acordado).
                  </p>
                )}
              </div>
            )}

            {/* Payments table */}
            {loadingPayments ? (
              <p className="text-sm text-zinc-400 italic">Cargando pagos...</p>
            ) : payments.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60 mb-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: ACCENT + "0d" }}>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Fecha
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Monto
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Método
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Notas
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 w-20">
                        Comprobante
                      </th>
                      {!isCancelled && !soloLectura && <th className="px-3 py-2"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">
                          {formatCalendarDate(p.fecha, timeZone) || "-"}
                        </td>
                        <td className="px-3 py-2 font-medium text-zinc-900 dark:text-white">
                          <EditableCell
                            value={p.monto}
                            formatDisplay={(v) => `$${Number(v || 0).toLocaleString("es-MX")}`}
                            inputMode="numeric"
                            disabled={isCancelled || soloLectura}
                            onSave={async (val) => {
                              if (!venueId) return;
                              const num = Number(val);
                              if (isNaN(num) || num <= 0) return;
                              const oldMonto = Number(p.monto) || 0;
                              const diff = num - oldMonto;
                              const batch = writeBatch(db);
                              batch.update(fsDoc(db, "venues", venueId, "payments", p.id), { monto: num });
                              const eventDocRef = fsDoc(db, "venues", venueId, "calendars", "default", "events", eventId);
                              batch.update(eventDocRef, { totalPagado: increment(diff) });
                              await batch.commit();
                              logBitacora(venueId, {
                                action: BITACORA_ACTIONS.PAYMENT_EDIT_AMOUNT,
                                entity: "payment",
                                entityId: eventId,
                                entityName: event?.nombre || "",
                                details: { oldMonto: oldMonto, newMonto: num, pagoId: p.id },
                              }, userInfo, user);
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">
                          {isCancelled || soloLectura ? (
                            <span>{p.metodo || <span className="text-zinc-400">-</span>}</span>
                          ) : (
                            <select
                              className="w-full rounded border border-transparent bg-transparent py-1 text-sm cursor-pointer hover:border-zinc-300 dark:hover:border-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#1b4d2e] focus:border-zinc-300"
                              value={p.metodo || ""}
                              onChange={async (e) => {
                                const val = e.target.value;
                                if (!venueId) return;
                                const pagoDocRef = fsDoc(db, "venues", venueId, "payments", p.id);
                                await updateDoc(pagoDocRef, { metodo: val });
                                logBitacora(venueId, {
                                  action: BITACORA_ACTIONS.PAYMENT_EDIT_METHOD,
                                  entity: "payment",
                                  entityId: eventId,
                                  entityName: event?.nombre || "",
                                  details: { metodo: val, pagoId: p.id },
                                }, userInfo, user);
                              }}
                            >
                              <option value="">-</option>
                              {metodosPago.map((m) => (
                                <option key={m.id} value={m.nombre}>{m.nombre}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300 max-w-[180px]">
                          <EditableCell
                            value={p.notas}
                            disabled={isCancelled || soloLectura}
                            onSave={async (val) => {
                              if (!venueId) return;
                              const pagoDocRef = fsDoc(db, "venues", venueId, "payments", p.id);
                              await updateDoc(pagoDocRef, { notas: val });
                              logBitacora(venueId, {
                                action: BITACORA_ACTIONS.PAYMENT_EDIT_NOTES,
                                entity: "payment",
                                entityId: eventId,
                                entityName: event?.nombre || "",
                                details: { pagoId: p.id },
                              }, userInfo, user);
                            }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <ComprobanteCell
                            venueId={venueId}
                            eventId={eventId}
                            eventNombre={event?.nombre}
                            linkedKind="payment"
                            linkedId={p.id}
                            linkedLabel={`Comprobante de pago ${formatCalendarDate(p.fecha, timeZone) || ""}`.trim()}
                            archivo={archivosByLinked[`payment:${p.id}`]}
                            currentUser={user}
                            userInfo={userInfo}
                            onChanged={reloadEventArchivos}
                            soloLectura={soloLectura}
                          />
                        </td>
                        {!isCancelled && !soloLectura && (
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              className="text-red-500 hover:text-red-700 text-xs font-medium"
                              onClick={() => handleRemovePago(p.id)}
                            >
                              Eliminar
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-zinc-400 italic mb-4">
                No hay pagos registrados.
              </p>
            )}

            {/* Add payment form — hidden when cancelled or en solo lectura */}
            {!isCancelled && !soloLectura && <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 p-4 mb-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-3">
                Registrar pago
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Fecha
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                    value={newPago.fecha}
                    onChange={(e) =>
                      setNewPago((p) => ({ ...p, fecha: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Monto
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none text-sm">
                      $
                    </span>
                    <input
                      type="text"
                      className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                      value={newPago.monto ? fmtMXN(newPago.monto) : ""}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1").replace(/(\.\d{2})\d+/, "$1");
                        setNewPago((p) => ({ ...p, monto: raw }));
                      }}
                      inputMode="decimal"
                      pattern="[0-9.,]*"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Método
                  </label>
                  {metodosPago.length > 0 ? (
                    <Listbox
                      value={newPago.metodo}
                      onChange={(val) => setNewPago((p) => ({ ...p, metodo: val }))}
                      className="w-full"
                      placeholder="Seleccionar..."
                    >
                      {metodosPago.map((m) => (
                        <ListboxOption key={m.id} value={m.nombre}>
                          {m.nombre}
                        </ListboxOption>
                      ))}
                    </Listbox>
                  ) : (
                    <input
                      type="text"
                      className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                      value={newPago.metodo}
                      onChange={(e) =>
                        setNewPago((p) => ({ ...p, metodo: e.target.value }))
                      }
                      placeholder="Efectivo, transferencia..."
                    />
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Notas
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                    value={newPago.notas}
                    onChange={(e) =>
                      setNewPago((p) => ({ ...p, notas: e.target.value }))
                    }
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowQuickAddMetodo((v) => !v)}
                className="mt-2 text-xs font-medium transition hover:underline"
                style={{ color: ACCENT }}
              >
                {showQuickAddMetodo ? "Cancelar" : "+ Nuevo método de pago"}
              </button>
              {showQuickAddMetodo && (
                <div className="mt-1.5 flex gap-2 max-w-xs">
                  <input
                    type="text"
                    className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                    value={quickMetodo}
                    onChange={(e) => setQuickMetodo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleQuickAddMetodo(quickMetodo, "pago"); }}
                    placeholder="Nombre del método"
                  />
                  <button
                    type="button"
                    onClick={() => handleQuickAddMetodo(quickMetodo, "pago")}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white"
                    style={{ backgroundColor: ACCENT }}
                  >
                    Crear
                  </button>
                </div>
              )}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleAddPago}
                  className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition"
                  style={{ backgroundColor: ACCENT }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#164026")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = ACCENT)
                  }
                >
                  Registrar pago
                </button>
              </div>
            </div>}

          </Section>
        </div>
        )}
          </div>
        )}

        {/* Depósitos en garantía (cliente) */}
        {eventTab === "balance" && !isViewer && (
          <EventClienteDepositos
            venueId={venueId}
            eventId={eventId}
            eventNombre={event?.nombre}
            soloLectura={soloLectura}
          />
        )}

        {/* Archivos internos del evento: cuelgan de General (FRA-357) */}
        {eventTab === "general" && !isViewer && (
          <div className="mb-6">
            <EventArchivos
              venueId={venueId}
              eventId={eventId}
              eventNombre={event?.nombre}
              soloLectura={soloLectura}
            />
          </div>
        )}

        {/* Seguimientos section — unified timeline: lead-side history +
            event lifecycle + payments + refunds + cotizaciones. Notas thread
            lives on the source lead's subcollection (single source of truth)
            for converted-from-lead events, or on the event's own notas
            subcollection for manually-created events. */}
        {eventTab === "seguimientos" && (() => {
          const eventFilters = [
            { entity: "event", entityId: eventId },
            { entity: "payment", entityId: eventId },
            { entity: "refund", entityId: eventId },
            { entity: "cotizacion", entityId: eventId },
          ];
          const filters = event.convertedFromLeadId
            ? [{ entity: "lead", entityId: event.convertedFromLeadId }, ...eventFilters]
            : eventFilters;

          if (event.convertedFromLeadId) {
            // Converted from a lead — write notas to the lead's subcollection
            // (preserves carry-over: lead-side and event-side see the same thread).
            return (
              <LeadNotasTimeline
                leadId={event.convertedFromLeadId}
                venueId={venueId}
                leadTopic={event.nombre || ""}
                isViewer={isViewer}
                readOnly={soloLectura}
                systemEventFilters={filters}
              />
            );
          }
          // Manually-created event — write notas to its own subcollection.
          const calendarId = event.calendarId || "default";
          return (
            <LeadNotasTimeline
              leadId={null}
              venueId={venueId}
              leadTopic={event.nombre || ""}
              isViewer={isViewer}
              readOnly={soloLectura}
              systemEventFilters={filters}
              notasPath={[
                "venues",
                venueId,
                "calendars",
                calendarId,
                "events",
                eventId,
                "notas",
              ]}
              notaBitacoraEntity="event_nota"
              notaActions={{
                create: BITACORA_ACTIONS.EVENT_NOTA_CREATE,
                edit: BITACORA_ACTIONS.EVENT_NOTA_EDIT,
                delete: BITACORA_ACTIONS.EVENT_NOTA_DELETE,
              }}
              notaParentDetails={{ eventId, eventName: event.nombre || "" }}
            />
          );
        })()}

      </form>

      {/* Cancel / Reactivate event buttons — only on the General tab, for "event" type, outside edit mode */}
      {eventTab === "general" && eventType === "event" && !isEditing && !soloLectura && (
        <div className="mt-8 flex justify-end">
          {!isCancelled && (
            <button
              type="button"
              onClick={() => setShowCancelConfirm(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 px-4 py-2 text-sm font-semibold text-red-700 dark:text-red-400 transition hover:bg-red-100 dark:hover:bg-red-900/40"
            >
              <XMarkIcon className="h-4 w-4" />
              Cancelar evento
            </button>
          )}
          {isCancelled && (
            <button
              type="button"
              onClick={() => setShowReactivateConfirm(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400 transition hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
            >
              Reactivar evento
            </button>
          )}
        </div>
      )}

      <DialogoConfirmacion
        open={confirmarBorrado}
        onClose={() => setConfirmarBorrado(false)}
        saving={borrando}
        icono={TrashOutlineIcon}
        titulo={
          event?.eventType === "bloqueo"
            ? "Eliminar bloqueo"
            : event?.eventType === "reunion"
            ? "Eliminar reunión"
            : "Eliminar scouting"
        }
        subtitulo={event?.nombre || ""}
        confirmar={{ label: "Eliminar", savingLabel: "Eliminando…", tono: "rojo", onClick: borrarDelCalendario }}
      >
        <p>Desaparece del calendario y libera esas fechas. Esta acción no se puede deshacer.</p>
      </DialogoConfirmacion>

      {/* Clash Confirmation Dialog */}
      <Alert
        open={showClashConfirm}
        onClose={() => setShowClashConfirm(false)}
      >
        <AlertTitle>Cruce de fechas</AlertTitle>
        <AlertDescription>
          Las fechas de este evento se cruzan con:
          <ul className="list-disc list-inside mt-2 space-y-0.5">
            {clashingEvents.map((ce) => {
              const typeLabel = EVENT_TYPE_CONFIG[ce.eventType]?.label;
              return (
                <li key={ce.id} className="font-medium">
                  {typeLabel && <span className="font-semibold">{typeLabel}:</span>}{" "}
                  {ce.nombre || "Sin nombre"}
                </li>
              );
            })}
          </ul>
          <span className="block mt-2">¿Deseas guardar de todos modos?</span>
        </AlertDescription>
        <AlertActions>
          <Button outline onClick={() => setShowClashConfirm(false)}>
            Cancelar
          </Button>
          <button
            onClick={() => {
              setShowClashConfirm(false);
              handleSave().then((ok) => {
                if (ok) setIsEditing(false);
              });
            }}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition"
            style={{ backgroundColor: ACCENT }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#164026")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = ACCENT)
            }
          >
            Guardar de todos modos
          </button>
        </AlertActions>
      </Alert>

      {/* Cancelar evento: qué pasa con el dinero, las piezas y las fechas */}
      <DialogoConfirmacion
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        titulo="Cancelar evento"
        subtitulo={event?.nombre || ""}
        icono={CancelarEventoIcon}
        saving={cancelling}
        cancelarLabel="Volver"
        confirmar={{
          label: "Confirmar cancelación",
          savingLabel: "Cancelando…",
          onClick: handleCancelEvent,
        }}
      >
        <p>
          El evento queda como cancelado: se liberan sus fechas y ya no se pueden
          registrar pagos nuevos. Esto es lo que pasa con lo que ya tiene:
        </p>
        <Resumen>
          {pendienteReembolso > 0 && (
            <Fila
              tono="ambar"
              monto={`$${pendienteReembolso.toLocaleString("es-MX")}`}
              nota="Siguen contando como ingreso en Cuentas. Si devuelves dinero al cliente, regístralo aquí mismo: al quedar cancelado, esta página te ofrece «Registrar reembolso»."
            >
              Pagos del cliente sin reembolsar
            </Fila>
          )}
          {planCancelacion.liberar.length > 0 && (
            <Fila
              tono="verde"
              monto={`$${planCancelacion.totalLiberar.toLocaleString("es-MX")}`}
              nota="Dejan de contar como deuda. Si reactivas el evento, los de la cotización se vuelven a generar; los capturados a mano no."
            >
              {planCancelacion.liberar.length === 1
                ? "Un costo por pagar se libera"
                : `${planCancelacion.liberar.length} costos por pagar se liberan`}
            </Fila>
          )}
          {planCancelacion.saldos.length > 0 && (
            <Fila
              tono="teal"
              monto={`$${planCancelacion.totalSaldos.toLocaleString("es-MX")}`}
              nota="Queda como saldo a favor en la ficha del proveedor: lo aplicas a otro evento o registras que te lo devolvió."
            >
              Ya pagado a {planCancelacion.saldos.length === 1 ? "un proveedor" : `${planCancelacion.saldos.length} proveedores`}
            </Fila>
          )}
          {planCancelacion.hundidos.length > 0 && (
            <Fila
              tono="zinc"
              monto={`$${planCancelacion.totalHundidos.toLocaleString("es-MX")}`}
              nota="Personal, insumos y similares: ya se gastó y se queda como gasto del evento."
            >
              Pagado sin proveedor
            </Fila>
          )}
          {unidadesDeEvento(event).length > 0 && (
            <Fila tono="verde">
              {unidadesDeEvento(event).length === 1
                ? "La pieza reservada queda libre"
                : `Las ${unidadesDeEvento(event).length} piezas reservadas quedan libres`}
            </Fila>
          )}
          {pendienteReembolso === 0 &&
            planCancelacion.liberar.length === 0 &&
            planCancelacion.saldos.length === 0 &&
            planCancelacion.hundidos.length === 0 &&
            unidadesDeEvento(event).length === 0 && (
              <Fila tono="zinc">Sin pagos, costos ni piezas: solo se libera la fecha.</Fila>
            )}
        </Resumen>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            Razón de cancelación (opcional)
          </label>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={2}
            placeholder="Ej. el cliente cambió de fecha y no hay disponibilidad"
            className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          />
        </div>
      </DialogoConfirmacion>

      {/* Eliminar reembolso */}
      {(() => {
        const r = eventRefunds.find((x) => x.id === refundToDeleteId) || null;
        return (
          <DialogoConfirmacion
            open={showDeleteRefundConfirm}
            onClose={cancelRemoveRefund}
            titulo="Eliminar reembolso"
            subtitulo={event?.nombre || ""}
            icono={TrashOutlineIcon}
            cancelarLabel="Cancelar"
            confirmar={{ label: "Eliminar reembolso", tono: "rojo", onClick: confirmRemoveRefund }}
          >
            <p>
              El reembolso desaparece del evento y de Cuentas: lo pagado por el
              cliente vuelve a contar completo como ingreso. Esta acción no se
              puede deshacer.
            </p>
            {r && (
              <Resumen>
                <Fila tono="rojo" monto={`$${(Number(r.monto) || 0).toLocaleString("es-MX")}`}>
                  {r.notas || "Reembolso"}
                </Fila>
                <Fila tono="zinc">
                  {formatCalendarDate(r.fecha, timeZone, { style: "short" }) || "—"}
                  {r.metodo ? ` · ${r.metodo}` : ""}
                </Fila>
                {archivosByLinked[`refund:${r.id}`] && (
                  <Fila tono="zinc">El comprobante adjunto también se elimina.</Fila>
                )}
              </Resumen>
            )}
          </DialogoConfirmacion>
        );
      })()}

      {/* Reactivate Event Confirmation Dialog */}
      <Alert
        open={showReactivateConfirm}
        onClose={() => setShowReactivateConfirm(false)}
      >
        <AlertTitle>Reactivar evento</AlertTitle>
        <AlertDescription>
          ¿Estás seguro de que deseas reactivar este evento? Las fechas volverán
          a estar ocupadas y se podrán registrar nuevos pagos.
          {eventRefunds.length > 0 && (
            <p className="mt-2 text-red-600 font-medium">
              Se eliminarán {eventRefunds.length} reembolso{eventRefunds.length > 1 ? "s" : ""} registrado{eventRefunds.length > 1 ? "s" : ""} (${totalReembolsado.toLocaleString("es-MX")}).
            </p>
          )}
          {reactivationClashes.length > 0 && (
            <div className="mt-2 rounded-lg border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3">
              <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">
                Las fechas se cruzan con:
              </div>
              <ul className="list-disc list-inside text-xs text-amber-600 dark:text-amber-400 space-y-0.5">
                {reactivationClashes.map((ce) => {
                  const typeLabel = EVENT_TYPE_CONFIG[ce.eventType]?.label;
                  return (
                    <li key={ce.id}>
                      {typeLabel && <span className="font-semibold">{typeLabel}:</span>}{" "}
                      {ce.nombre || "Sin nombre"}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </AlertDescription>
        <AlertActions>
          <Button outline onClick={() => setShowReactivateConfirm(false)} disabled={reactivating}>
            Cancelar
          </Button>
          <button
            onClick={handleReactivateEvent}
            disabled={reactivating}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled)
                e.currentTarget.style.backgroundColor = "#164026";
            }}
            onMouseLeave={(e) => {
              if (!e.currentTarget.disabled)
                e.currentTarget.style.backgroundColor = ACCENT;
            }}
          >
            {reactivating ? "Reactivando..." : "Confirmar reactivación"}
          </button>
        </AlertActions>
      </Alert>

      {/* Eliminar pago */}
      {(() => {
        const pg = payments.find((x) => x.id === pagoToDeleteIdx) || null;
        return (
          <DialogoConfirmacion
            open={showDeletePagoConfirm}
            onClose={cancelRemovePago}
            titulo="Eliminar pago"
            subtitulo={event?.nombre || ""}
            icono={TrashOutlineIcon}
            cancelarLabel="Cancelar"
            confirmar={{ label: "Eliminar pago", tono: "rojo", onClick: confirmRemovePago }}
          >
            <p>
              El pago desaparece del evento y de Cuentas, y el saldo por cobrar
              del evento sube en ese monto. Esta acción no se puede deshacer.
            </p>
            {pg && (
              <Resumen>
                <Fila tono="rojo" monto={`$${(Number(pg.monto) || 0).toLocaleString("es-MX")}`}>
                  {pg.notas || "Pago"}
                </Fila>
                <Fila tono="zinc">
                  {formatCalendarDate(pg.fecha, timeZone, { style: "short" }) || "—"}
                  {pg.metodo ? ` · ${pg.metodo}` : ""}
                </Fila>
                {archivosByLinked[`payment:${pg.id}`] && (
                  <Fila tono="zinc">El comprobante adjunto también se elimina.</Fila>
                )}
              </Resumen>
            )}
          </DialogoConfirmacion>
        );
      })()}
    </div>
  );
}
